/**
 * Phase B infra panels — Redis, Collabora, Loadbalancers, DNS providers.
 *
 * Lives in a sibling file to InfrastructurePage.jsx so the main page stays
 * under ~500 lines. The 4 panels share the same shape:
 *   • list resources via SWR
 *   • per-row inline test/delete buttons
 *   • simple "Add …" form rendered above the list
 *
 * Pool panels (Redis, Collabora) additionally manage members.
 */
import React, { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Spinner, Empty, StatusBadge } from "@/components/Atoms";
import { Plus, Trash2, FlaskConical, Activity, RefreshCw, ShieldCheck } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function Field({ label, v, on, testid, type = "text", min }) {
    return (
        <label className="block">
            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">{label}</span>
            <input
                type={type}
                value={v ?? ""}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                min={min}
                className="cm-input w-full"
            />
        </label>
    );
}

/**
 * Renders any `service_vms?kind=…` rows that are NOT yet `active` so the
 * operator can SEE provisioning / failed VMs at a glance — without
 * having to dig into the Service-VMs list. Auto-polls every 5s while
 * any row is still in-flight.
 *
 * `extraKinds` lets a panel pull in related kinds (e.g. redis-lb
 * placeholder shows up in the Redis panel because it was auto-
 * bootstrapped as a dependency).
 *
 *   ╔══════════════════════════════════════════════════════════════════╗
 *   ║ In-flight & failed redis VMs (3)             [Refresh] [Cleanup] ║
 *   ║ ──────────────────────────────────────────────────────────────── ║
 *   ║ cm-redis-a1b2 · vmid 102 · 10.16.0.12  · provisioning           ║
 *   ║ cm-redis-c3d4 · vmid (—) · ip (—)      · failed  [Delete]       ║
 *   ║   ↳ ipam: pool exhausted (10.16.0.10-10.16.0.50)                 ║
 *   ╚══════════════════════════════════════════════════════════════════╝
 */
function InFlightServiceVms({ kind, testidPrefix, extraKind = null }) {
    // Up to 2 fixed SWR hooks (kind + optional extraKind) — keeps the Rules-of-Hooks
    // contract straightforward (no dynamic-length hook arrays).
    const primary = useSWR(`/api/admin/infra/service-vms?kind=${kind}`, fetcher, { refreshInterval: 5000 });
    const secondary = useSWR(
        extraKind ? `/api/admin/infra/service-vms?kind=${extraKind}` : null,
        fetcher,
        { refreshInterval: 5000 },
    );
    const allRows = [...(primary.data || []), ...(secondary.data || [])];
    const rows = allRows.filter((r) => r.status !== "active");
    const mutateAll = () => { primary.mutate(); secondary.mutate(); };
    const [cleaning, setCleaning] = useState(false);
    const kindsLabel = extraKind ? `${kind}/${extraKind}` : kind;

    const onDelete = async (id) => {
        if (!confirm("Delete this VM row?\n\nIf it's a real VM that booted, Proxmox will also destroy it.")) return;
        try {
            await api.delete(`/api/admin/infra/service-vms/${id}`);
            mutateAll();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };

    const onCleanup = async () => {
        if (!confirm("Cleanup ALL orphan rows (status=failed/provisioning, no vmid, older than 5min)?\n\nProxmox VMs are NOT touched — this only removes stale DB placeholders.")) return;
        setCleaning(true);
        try {
            const r = await api.post("/api/admin/infra/service-vms/cleanup-orphans");
            alert(`Cleanup done.\n\nDeleted: ${r.data.deleted_count}\nSkipped (too young): ${r.data.skipped_count}`);
            mutateAll();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        } finally {
            setCleaning(false);
        }
    };

    const onHealth = async (id) => {
        try {
            const r = await api.post(`/api/admin/infra/service-vms/${id}/health`);
            const s = r?.data || {};
            if (s.status === "active") {
                alert(`Health OK — VM is now ACTIVE${s.pool_member_registered ? " and registered in its pool" : ""}.\n\n${s.detail || ""}`);
            } else {
                alert(`Health check returned: ${s.status || "?"}\n\n${s.detail || "(no detail)"}\n\nThe VM stays in-flight; the background worker keeps retrying every ~30s.`);
            }
            mutateAll();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        }
    };

    if (rows.length === 0) return null;
    const hasOrphans = rows.some((r) => !r.vmid && ["failed", "error", "provisioning", "unhealthy"].includes(r.status));

    return (
        <div className="cm-card p-3 border border-amber-700/40 bg-amber-950/10" data-testid={`${testidPrefix}-inflight-panel`}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="cm-h4 text-amber-300">
                    In-flight & failed {kindsLabel} VMs ({rows.length})
                </div>
                <div className="flex gap-1">
                    <button onClick={mutateAll}
                        className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1"
                        data-testid={`${testidPrefix}-inflight-refresh`}>
                        <RefreshCw size={10} /> Refresh
                    </button>
                    {hasOrphans && (
                        <button onClick={onCleanup} disabled={cleaning}
                            className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 text-amber-300 border border-amber-700/40"
                            data-testid={`${testidPrefix}-inflight-cleanup`}>
                            <Trash2 size={10} /> {cleaning ? "Cleaning…" : "Cleanup orphans"}
                        </button>
                    )}
                </div>
            </div>
            <div className="space-y-1">
                {rows.map((r) => {
                    const isFail = r.status === "failed" || r.status === "error";
                    return (
                        <div key={r.id}
                            className={`text-xs font-mono p-2 border ${isFail ? "border-red-700/60 bg-red-950/30" : "border-zinc-700 bg-zinc-950"}`}
                            data-testid={`${testidPrefix}-inflight-${r.id}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="truncate text-zinc-200">
                                    <span className="mr-2 px-1.5 py-0.5 text-[10px] uppercase bg-zinc-800">{r.status}</span>
                                    <span className="mr-2 text-zinc-500">[{r.kind}]</span>
                                    {r.hostname || "(no hostname)"} · vmid {r.vmid || "—"} · ip {r.ip_internal || "—"}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {r.vmid && !isFail && (
                                        <button onClick={() => onHealth(r.id)}
                                            className="cm-btn-ghost text-[11px] px-1.5 py-0.5 text-emerald-400"
                                            title="Run health check now (promotes to ACTIVE + registers in pool if the service responds)"
                                            data-testid={`${testidPrefix}-inflight-health-${r.id}`}>
                                            <Activity size={10} />
                                        </button>
                                    )}
                                    <button onClick={() => onDelete(r.id)}
                                        className="cm-btn-ghost text-[11px] px-1.5 py-0.5 text-red-400"
                                        data-testid={`${testidPrefix}-inflight-delete-${r.id}`}>
                                        <Trash2 size={10} />
                                    </button>
                                </div>
                            </div>
                            {isFail && r.error && (
                                <div className="text-red-300 text-[11px] mt-1 break-all">
                                    ↳ {r.error}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// =====================================================================
// Redis pools
// =====================================================================
export function RedisPoolPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/redis-pools", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [showAddVm, setShowAddVm] = useState(false);
    const [memberPool, setMemberPool] = useState(null);
    const [tests, setTests] = useState({});

    const onTest = async (poolId, mid) => {
        setTests((t) => ({ ...t, [mid]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/redis-pools/${poolId}/members/${mid}/test`);
            setTests((t) => ({ ...t, [mid]: r.data }));
        } catch (e) {
            setTests((t) => ({ ...t, [mid]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };
    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="flex justify-end gap-2">
                <button data-testid="redis-add-vm-btn" onClick={() => setShowAddVm(true)} className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1">
                    <Plus size={12} /> Add Redis VM
                </button>
                <button data-testid="redis-pool-add-btn" onClick={() => setShowForm(true)} className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                         title="Advanced — register an external Redis pool (won't be auto-provisioned)">
                    <Plus size={12} /> Add external pool
                </button>
            </div>
            {showAddVm && (
                <AddServiceVmFlow
                    kind="redis"
                    onClose={() => { setShowAddVm(false); mutate(); }}
                />
            )}
            {showForm && (
                <PoolForm
                    apiBase="/api/admin/infra/redis-pools"
                    testidPrefix="redis"
                    title="New Redis pool"
                    typeOptions={[
                        { v: "standalone", label: "standalone — single Redis" },
                        { v: "cluster", label: "cluster — Redis Cluster" },
                        { v: "sentinel", label: "sentinel — Redis Sentinel" },
                    ]}
                    defaults={{ name: "", region: "default", type: "standalone", max_clouds: 500 }}
                    onClose={() => { setShowForm(false); mutate(); }}
                />
            )}
            {memberPool && (
                <RedisMemberForm pool={memberPool} onClose={() => { setMemberPool(null); mutate(); }} />
            )}
            <InFlightServiceVms kind="redis" testidPrefix="redis" extraKind="redis-lb" />
            {data?.length === 0 ? (
                <Empty title="No Redis VM yet" hint="Add one — every tenant gets a key prefix isolated per cloud. VM = independent Redis instance with its own capacity." />
            ) : (
                <div className="space-y-4">
                    {data.map((p) => (
                        <PoolCard key={p.id} pool={p} apiBase="/api/admin/infra/redis-pools"
                                   metaLine={`Redis VM · ${p.region} · ${p.current_clouds}/${p.max_clouds} clouds · ${p.type}`}
                                   onMutate={mutate} onAddMember={() => setMemberPool(p)}
                                   memberDisplay={(m) => `${m.host}:${m.port}  pw ${m.password_masked || "—"}`}
                                   memberTests={tests} onTestMember={onTest}
                                   addMemberLabel={p.type === "standalone" ? "Add endpoint" : "Add node"}
                                   testidPrefix="redis"
                                   provisionKind="redis"
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function RedisMemberForm({ pool, onClose }) {
    const [form, setForm] = useState({ node_name: "", host: "", port: 6379, password: "" });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try { await api.post(`/api/admin/infra/redis-pools/${pool.id}/members`, form); onClose(); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="redis-member-form">
            <div className="cm-h3 mb-2">Add Redis to {pool.name}</div>
            <Field label="Node name (optional)" v={form.node_name} on={(v) => setForm({ ...form, node_name: v })} testid="rm-node" />
            <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><Field label="Host" v={form.host} on={(v) => setForm({ ...form, host: v })} testid="rm-host" /></div>
                <Field label="Port" type="number" v={form.port} on={(v) => setForm({ ...form, port: parseInt(v, 10) || 6379 })} testid="rm-port" />
            </div>
            <Field label="Password (optional)" type="password" v={form.password} on={(v) => setForm({ ...form, password: v })} testid="rm-pw" />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <FormButtons onClose={onClose} testid="rm" />
        </form>
    );
}

// =====================================================================
// Collabora pools
// =====================================================================
export function CollaboraPoolPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/collabora-pools", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [showAddVm, setShowAddVm] = useState(false);
    const [memberPool, setMemberPool] = useState(null);
    const [tests, setTests] = useState({});

    const onTest = async (poolId, mid) => {
        setTests((t) => ({ ...t, [mid]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/collabora-pools/${poolId}/members/${mid}/test`);
            setTests((t) => ({ ...t, [mid]: r.data }));
        } catch (e) {
            setTests((t) => ({ ...t, [mid]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };
    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">Collabora VMs</strong> — each VM is an independent Collabora Online instance
                with its own capacity (<code>max_clouds</code>). Tenants stick to one VM.
                For more capacity: deploy another VM via &quot;Add Collabora VM&quot;.
                Alle VMs leben im Service-Zone-Overlay (intern, ohne Public-IP).
            </div>
            <div className="flex justify-end gap-2">
                <button data-testid="collabora-repush-btn"
                    onClick={async () => {
                        if (!confirm("WOPI-Allowlist auf allen Collabora-VMs neu schreiben und Config an ALLE gebundenen Clouds neu pushen?")) return;
                        try {
                            const r = await api.post("/api/admin/infra/collabora/repush");
                            alert(`Collabora re-push: ${r.data.bumped}/${r.data.clouds} Clouds · ${(r.data.allowlists || []).length} VM-Allowlists neu geschrieben.`);
                        } catch (e) { alert(e?.response?.data?.detail || String(e)); }
                    }}
                    className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                    title="Re-sync WOPI allow-list on all Collabora VMs + re-apply config to every bound cloud">
                    <RefreshCw size={12} /> Re-push (alle Clouds)
                </button>
                <button data-testid="whiteboard-repush-btn"
                    onClick={async () => {
                        if (!confirm("Re-push the whiteboard configuration to ALL clouds with whiteboard enabled?")) return;
                        try {
                            const r = await api.post("/api/admin/infra/whiteboard/repush");
                            alert(`Whiteboard re-push: ${r.data.bumped}/${r.data.clouds} Clouds neu konfiguriert.`);
                        } catch (e) { alert(e?.response?.data?.detail || String(e)); }
                    }}
                    className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                    title="Re-apply whiteboard config to every cloud with whiteboard enabled">
                    <RefreshCw size={12} /> Whiteboard: Re-push
                </button>
                <button data-testid="collabora-add-vm-btn" onClick={() => setShowAddVm(true)} className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1">
                    <Plus size={12} /> Add Collabora VM
                </button>
                <button data-testid="collabora-pool-add-btn" onClick={() => setShowForm(true)} className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1"
                         title="Advanced — register an external Collabora endpoint (no VM gets provisioned)">
                    <Plus size={12} /> Advanced: external endpoint
                </button>
            </div>
            {showAddVm && (
                <AddServiceVmFlow kind="collabora" onClose={() => { setShowAddVm(false); mutate(); }} />
            )}
            {showForm && (
                <PoolForm
                    apiBase="/api/admin/infra/collabora-pools"
                    testidPrefix="collabora"
                    title="New Collabora pool"
                    typeOptions={[
                        { v: "standalone", label: "standalone — single Collabora" },
                        { v: "cluster", label: "cluster — multi-node" },
                    ]}
                    defaults={{ name: "", region: "default", type: "standalone", max_clouds: 200 }}
                    onClose={() => { setShowForm(false); mutate(); }}
                />
            )}
            {memberPool && (
                <CollaboraMemberForm pool={memberPool} onClose={() => { setMemberPool(null); mutate(); }} />
            )}
            <InFlightServiceVms kind="collabora" testidPrefix="collabora" />
            {data?.length === 0 ? (
                <Empty title="No Collabora VM yet" hint="Add Collabora VMs — tenants stick to one. VM = independent Collabora instance with its own capacity." />
            ) : (
                <div className="space-y-4">
                    {data.map((p) => (
                        <PoolCard key={p.id} pool={p} apiBase="/api/admin/infra/collabora-pools"
                                   metaLine={`Collabora VM · ${p.region} · ${p.current_clouds}/${p.max_clouds} clouds · ${p.type}`}
                                   onMutate={mutate} onAddMember={() => setMemberPool(p)}
                                   memberDisplay={(m) => `${m.public_url} (${m.host}:${m.port})`}
                                   memberTests={tests} onTestMember={onTest}
                                   addMemberLabel="Add endpoint"
                                   testidPrefix="collabora"
                                   provisionKind="collabora"
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function CollaboraMemberForm({ pool, onClose }) {
    const [form, setForm] = useState({ node_name: "", host: "", port: 9980, public_url: "", use_tls: true });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try { await api.post(`/api/admin/infra/collabora-pools/${pool.id}/members`, form); onClose(); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="collabora-member-form">
            <div className="cm-h3 mb-2">Add Collabora to {pool.name}</div>
            <Field label="Node name (optional)" v={form.node_name} on={(v) => setForm({ ...form, node_name: v })} testid="cm-node" />
            <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><Field label="Host" v={form.host} on={(v) => setForm({ ...form, host: v })} testid="cm-host" /></div>
                <Field label="Port" type="number" v={form.port} on={(v) => setForm({ ...form, port: parseInt(v, 10) || 9980 })} testid="cm-port" />
            </div>
            <Field label="Public URL (what NC contacts)" v={form.public_url} on={(v) => setForm({ ...form, public_url: v })} testid="cm-url" />
            <label className="flex items-center gap-2 text-xs text-zinc-300">
                <input type="checkbox" checked={form.use_tls} onChange={(e) => setForm({ ...form, use_tls: e.target.checked })} data-testid="cm-tls" />
                Use TLS
            </label>
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <FormButtons onClose={onClose} testid="cm" />
        </form>
    );
}

export function EuroOfficePoolPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/eurooffice-pools", fetcher);
    const [showAddVm, setShowAddVm] = useState(false);
    const [showForm, setShowForm] = useState(false);
    return (
        <div className="space-y-4" data-testid="eurooffice-pool-panel">
            <div className="flex items-center justify-end gap-2 flex-wrap">
                <button data-testid="eurooffice-add-vm-btn" onClick={() => setShowAddVm(true)}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1">
                    <Plus size={12} /> Add Euro-Office VM
                </button>
            </div>
            {showAddVm && (
                <AddServiceVmFlow kind="nextcloud-eurooffice" onClose={() => { setShowAddVm(false); mutate(); }} />
            )}
            <InFlightServiceVms kind="nextcloud-eurooffice" testidPrefix="eurooffice" />
            {data?.length === 0 ? (
                <Empty title="No Euro-Office VM yet"
                    hint="Add Euro-Office DS VMs — tenants choose Euro-Office OR Collabora. VM = independent DocumentServer (OnlyOffice-Fork, AGPL) with its own capacity." />
            ) : (
                <div className="space-y-4">
                    {(data || []).map((p) => (
                        <PoolCard key={p.id} pool={p} apiBase="/api/admin/infra/eurooffice-pools"
                            metaLine={`Euro-Office DS · ${p.region} · ${p.current_clouds}/${p.max_clouds} clouds · ${p.type}`}
                            onMutate={mutate} memberDisplay={(m) => `${m.host || ""}`}
                            testidPrefix="eurooffice" provisionKind="nextcloud-eurooffice" />
                    ))}
                </div>
            )}
        </div>
    );
}

// =====================================================================
// =====================================================================
// CM-Managed Caddy LBs (Service-VM kind=caddy)
// =====================================================================
export function CaddyLbPanel() {
    const { data: vms, mutate: mutateVms } = useSWR(
        "/api/admin/infra/service-vms?kind=caddy", fetcher,
        { refreshInterval: 5000 },
    );
    const { data: routes, mutate: mutateRoutes } = useSWR(
        "/api/admin/lb/routes", fetcher,
    );
    const { data: landing, mutate: mutateLanding } = useSWR(
        "/api/admin/lb/landing/status", fetcher,
    );
    const { data: ipPoolsRaw } = useSWR("/api/admin/infra/host-networks", fetcher);
    const publicPools = (ipPoolsRaw || []).filter(p => (p.purpose || "public") === "public");
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const clusterId = clusters?.[0]?.id;
    const { data: lbZone, mutate: mutateLbZone } = useSWR(
        clusterId ? `/api/admin/infra/lb-zone?cluster_id=${clusterId}` : null,
        fetcher,
        { refreshInterval: 5000 },
    );
    const { data: haPairs, mutate: mutateHa } = useSWR(
        "/api/admin/infra/lb/ha/pairs", fetcher, { refreshInterval: 8000 },
    );
    const [showDeploy, setShowDeploy] = useState(false);
    const [haCluster, setHaCluster] = useState(false);   // deploy 2 LBs at once
    const [showAddRoute, setShowAddRoute] = useState(false);
    const [probing, setProbing] = useState({});       // { [vm_id]: true }
    const [reapplying, setReapplying] = useState(false);
    const lbs = vms || [];
    // HA-Zuordnung vmid → {role, vip, group_id} aus den HA-Gruppen ableiten,
    // damit die LB-Karten ein Master/Backup+VIP-Badge zeigen können.
    const haByVmid = {};
    (haPairs || []).forEach((g) => {
        if (g.master?.vmid) haByVmid[String(g.master.vmid)] =
            { role: "master", vip: g.vip, group_id: g.group_id };
        (g.backups || []).forEach((b) => {
            if (b.vmid) haByVmid[String(b.vmid)] =
                { role: "backup", vip: g.vip, group_id: g.group_id };
        });
    });

    // Auto-probe ANY service-VM still in "installing" (or "starting") —
    // both the redis-lb (auto-bootstrapped) AND the Caddy LBs. Once
    // bootstrap finishes inside the VM this flips status → "active" via
    // qemu-guest-exec; without this the UI sits on "installing" forever.
    useEffect(() => {
        const targets = [
            ...(lbs || []).filter((l) => ["installing", "starting", "provisioning"].includes(l.status) && !probing[l.id]),
            ...(lbZone?.redis_lb && ["installing", "starting", "provisioning"].includes(lbZone.redis_lb.status) && !probing[lbZone.redis_lb.id]
                ? [{ id: lbZone.redis_lb.id }] : []),
        ];
        if (targets.length === 0) return;
        targets.forEach((t) => {
            setProbing((p) => ({ ...p, [t.id]: true }));
            api.post(`/api/admin/infra/service-vms/${t.id}/health`)
                .catch(() => {})
                .finally(() => {
                    setProbing((p) => { const c = { ...p }; delete c[t.id]; return c; });
                    mutateVms(); mutateLbZone();
                });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify([
        ...lbs.map((l) => `${l.id}:${l.status}`),
        lbZone?.redis_lb ? `${lbZone.redis_lb.id}:${lbZone.redis_lb.status}` : null,
    ])]);

    const onProbe = async (id) => {
        setProbing((p) => ({ ...p, [id]: true }));
        try {
            await api.post(`/api/admin/infra/service-vms/${id}/health`);
        } finally {
            setProbing((p) => { const c = { ...p }; delete c[id]; return c; });
            mutateVms();
        }
    };

    const onDelete = async (id) => {
        if (!confirm("Destroy this LB VM?")) return;
        await api.delete(`/api/admin/infra/service-vms/${id}`);
        mutateVms();
    };

    const onRebuild = async (id) => {
        if (!confirm(
            "LB sicher neu aufbauen (Routen-Rehydrate)?\n\n" +
            "Eine frische LB-VM wird geklont, ALLE Routen werden aus der DB " +
            "rehydrated and checked — only after a successful health check " +
            "is the old LB deleted. On failure the old LB remains " +
            "unangetastet. Fortschritt unter Jobs.\n\n" +
            "The public IP is preserved: for HA LBs the " +
            "VRRP floating IP stays on the peers; for single LBs the " +
            "die neue VM nach dem Cutover die alte IP (kurzer Re-IP-Reboot). " +
            "DNS A record does NOT need to change."
        )) return;
        try {
            const r = await api.post(`/api/admin/lb/${id}/rebuild`);
            alert(`LB-Neuaufbau gestartet.\nJob: ${r.data.job_id}\n\n` +
                  `Fortschritt unter „Jobs".`);
            mutateVms();
        } catch (e) {
            alert("Fehler: " + (e?.response?.data?.detail || String(e)));
        }
    };

    const onReapplyRoutes = async () => {
        if (!confirm(
            "Alle LB-Re-apply routes (stream_close_delay-Rollout)?\n\n" +
            "Every route is re-pushed to the LB(s) — this triggers ONE " +
            "Caddy-Reload aus und kappt dabei EINMALIG laufende Calls. " +
            "Afterwards, ongoing Talk calls survive future cloud/Souvera " +
            "actions.\n\nPlease run in a quiet time window."
        )) return;
        setReapplying(true);
        try {
            const r = await api.post("/api/admin/lb/reapply-routes");
            const list = r.data.loadbalancers || [];
            const pushed = list.reduce((s, l) => s + (l.pushed?.length || 0), 0);
            const failed = list.reduce((s, l) => s + (l.failed?.length || 0), 0);
            alert(
                `Routen neu angewendet auf ${list.length} LB(s).\n` +
                `Pushed: ${pushed} · Failed: ${failed}` +
                (list.length === 0
                    ? "\n\n(No Caddy LB registered — run on the live CM.)"
                    : "")
            );
            mutateRoutes();
        } catch (e) {
            alert("Fehler: " + (e?.response?.data?.detail || String(e)));
        } finally {
            setReapplying(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">CM-managed Caddy Loadbalancers</strong> —
                triple-NIC VMs (public LAN + service-zone + lb-zone). All Caddys in the
                cluster share Let&apos;s Encrypt certs &amp; ACME challenge state via a
                dedicated Redis (auto-deployed on first LB-deploy). Routes pushed via
                the Admin API (locked to 127.0.0.1, accessed by CM via guest-exec).
                <span className="block mt-1 text-amber-300/80">
                    💡 Redis-tenants &amp; Collabora live in the service-zone VXLAN
                    (<code>10.16.0.0/24</code>) — they NEVER consume public IPs.
                    The LB-zone Redis (<code>10.17.0.0/24</code>) is completely
                    separate, only Caddys can reach it.
                </span>
            </div>

            {/* Re-apply all routes (rolls out stream_close_delay onto existing LBs) */}
            <div className="cm-card p-3 border border-zinc-800 bg-zinc-950 flex items-center justify-between gap-3 flex-wrap" data-testid="lb-reapply-card">
                <div className="text-xs text-zinc-400 leading-relaxed max-w-2xl">
                    <strong className="text-zinc-200">Re-apply routes</strong> —
                    pusht alle persistierten Routen neu auf die LB(s) und rollt damit
                    <code className="mx-1">stream_close_delay</code> aus. EINMALIG nach dem
                    Deploy of this fix required: afterwards, ongoing Talk calls survive future
                    Cloud-/Souvera-Aktionen.
                    <span className="block mt-1 text-amber-300/80">
                        ⚠ Triggers a one-time Caddy reload (cuts active calls once)
                        — run in a quiet time window.
                    </span>
                </div>
                <button
                    data-testid="lb-reapply-routes-btn"
                    onClick={onReapplyRoutes}
                    disabled={reapplying}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                >
                    <RefreshCw size={12} className={reapplying ? "animate-spin" : ""} />
                    {reapplying ? "Wird angewendet…" : "Re-apply routes"}
                </button>
            </div>

            {/* LB-Zone status banner */}
            {lbZone && (
                <div className={`cm-card p-3 text-xs border ${
                    lbZone.ready
                        ? "bg-emerald-950/30 border-emerald-700 text-emerald-200"
                        : (lbZone.zone || lbZone.redis_lb)
                            ? "bg-amber-950/30 border-amber-700 text-amber-200"
                            : "bg-zinc-950 border-zinc-700 text-zinc-400"
                }`} data-testid="lb-zone-status">
                    <div className="font-mono">
                        <strong>LB-Zone:</strong>{" "}
                        {lbZone.zone ? `${lbZone.zone.zone} (${lbZone.zone.status})` : "not bootstrapped"}
                        {" · "}
                        <strong>Redis-LB:</strong>{" "}
                        {lbZone.redis_lb
                            ? `${lbZone.redis_lb.ip || "(no IP)"} · ${lbZone.redis_lb.status}`
                            : "not deployed"}
                    </div>
                    {!lbZone.ready && (
                        <div className="mt-1 text-[11px] opacity-80">
                            {lbZone.zone && lbZone.redis_lb
                                ? "Bootstrap in progress — Redis-LB is still spinning up."
                                : "Hit \"Deploy Caddy LB\" below to auto-bootstrap the LB-zone + Redis-LB before the first Caddy."}
                        </div>
                    )}
                </div>
            )}

            {/* Generic landing page for unknown / unconfigured domains */}
            <div className="cm-card p-3 border border-zinc-800 bg-zinc-950" data-testid="lb-landing-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-zinc-400 leading-relaxed max-w-2xl">
                        <strong className="text-zinc-200">Generische Landingpage (unbekannte Domains)</strong> —
                        ein Catch-All auf allen LBs zeigt eine neutrale „Diese Domain ist
                        not set up here" page for hosts pointing at the LB,
                        aber keinem Tenant zugeordnet sind (statt Caddy-Default/TLS-Fehler).
                        <span className="block mt-1">
                            Status:{" "}
                            <span className={landing?.enabled ? "text-emerald-300" : "text-zinc-400"}
                                data-testid="lb-landing-state">
                                {landing?.enabled ? "AKTIV" : "inaktiv"}
                            </span>
                            {typeof landing?.lb_count === "number" && ` · ${landing.lb_count} LB(s)`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <a href="/api/admin/lb/landing-preview" target="_blank" rel="noreferrer"
                            className="cm-btn-ghost text-xs px-3 py-2" data-testid="lb-landing-preview-btn">
                            Vorschau
                        </a>
                        {landing?.enabled ? (
                            <button data-testid="lb-landing-disable-btn"
                                onClick={async () => {
                                    if (!confirm("Generische Landingpage auf allen LBs entfernen?")) return;
                                    try { await api.post("/api/admin/lb/landing/disable"); mutateLanding(); }
                                    catch (e) { alert(e?.response?.data?.detail || String(e)); }
                                }}
                                className="cm-btn-ghost text-xs px-3 py-2 border border-red-800 text-red-300">
                                Disable
                            </button>
                        ) : (
                            <button data-testid="lb-landing-enable-btn"
                                onClick={async () => {
                                    try { const r = await api.post("/api/admin/lb/landing/enable"); mutateLanding(); alert(`Landingpage aktiviert auf ${(r.data.pushed || []).length} LB(s).`); }
                                    catch (e) { alert(e?.response?.data?.detail || String(e)); }
                                }}
                                className="cm-btn-primary text-xs px-3 py-2" >
                                Enable
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* HA-Pairing (Aktiv/Passiv mit gemeinsamer VIP via keepalived/VRRP) */}
            <CaddyHaPanel
                lbs={lbs}
                haPairs={haPairs}
                mutateHa={mutateHa}
                mutateVms={mutateVms}
                onDeploySecond={() => { setHaCluster(false); setShowDeploy(true); }}
            />


            <div className="flex justify-end gap-2">
                <button
                    data-testid="caddy-deploy-cluster-btn"
                    onClick={() => { setHaCluster(true); setShowDeploy(true); }}
                    className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1 border border-emerald-700 text-emerald-300"
                >
                    <Plus size={12} /> Deploy LB Cluster (HA, 2 VMs)
                </button>
                <button
                    data-testid="caddy-deploy-single-btn"
                    onClick={() => { setHaCluster(false); setShowDeploy(true); }}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1"
                >
                    <Plus size={12} /> Deploy Caddy LB
                </button>
            </div>
            {showDeploy && (
                <CaddyDeployForm
                    clusters={clusters}
                    publicPools={publicPools}
                    haCluster={haCluster}
                    onClose={() => { setShowDeploy(false); mutateVms(); }}
                />
            )}

            {lbs.length === 0 && !showDeploy && (
                <div className="cm-card p-6 text-center text-zinc-500 text-xs">
                    No Caddy LBs yet. Click &quot;Deploy LB Cluster&quot; for HA pair (recommended)
                    or &quot;Deploy Caddy LB&quot; for a single instance.
                </div>
            )}

            {/* Per-LB cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {lbs.map((lb) => (
                    <CaddyLbCard
                        key={lb.id}
                        lb={lb}
                        haInfo={haByVmid[String(lb.vmid)]}
                        routeCount={(routes || []).filter((r) => r.assigned_lb_vm_id === lb.id).length}
                        probing={probing[lb.id]}
                        onProbe={() => onProbe(lb.id)}
                        onDelete={() => onDelete(lb.id)}
                        onRebuild={() => onRebuild(lb.id)}
                        onHostnameSaved={() => mutateVms()}
                    />
                ))}
            </div>

            {/* Routes list — shared across all LBs */}
            {lbs.length > 0 && (
                <div className="cm-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="cm-h3">Tenant Routes (active on all LBs)</div>
                        <button
                            data-testid="caddy-route-add-btn"
                            onClick={() => setShowAddRoute(true)}
                            className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
                        >
                            <Plus size={12} /> Add Route
                        </button>
                    </div>
                    {showAddRoute && (
                        <CaddyAddRouteForm
                            lb={lbs[0]}
                            onClose={() => { setShowAddRoute(false); mutateRoutes(); }}
                        />
                    )}
                    {(routes || []).length === 0 ? (
                        <div className="text-zinc-500 text-xs italic py-3">No routes yet.</div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="text-zinc-500 text-[11px] uppercase tracking-wider">
                                <tr><th className="text-left py-1">Host</th><th className="text-left py-1">Upstream</th><th className="text-left py-1">Assigned LB</th><th className="text-left py-1">CNAME target</th><th></th></tr>
                            </thead>
                            <tbody>
                                {(routes || []).map((r) => {
                                    const target = r.lb_dns_target || {};
                                    const assignedLb = lbs.find((l) => l.id === r.assigned_lb_vm_id);
                                    const status = r.status || "active";
                                    return (
                                        <tr key={r.id} className="border-t border-zinc-900" data-testid={`caddy-route-${r.host}`}>
                                            <td className="py-2 font-mono">
                                                <div className="flex items-center gap-2">
                                                    <span>{r.host}</span>
                                                    {status === "pending" ? (
                                                        <span className="text-[10px] uppercase tracking-wider bg-amber-900/40 text-amber-200 px-1.5 py-0.5 rounded" data-testid={`caddy-route-status-${r.host}`}>
                                                            pending
                                                        </span>
                                                    ) : (
                                                        <span className="text-[10px] uppercase tracking-wider bg-emerald-900/40 text-emerald-200 px-1.5 py-0.5 rounded" data-testid={`caddy-route-status-${r.host}`}>
                                                            active
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-2 font-mono">
                                                {r.mode === "placeholder" ? (
                                                    <span className="text-amber-300">⏳ placeholder splash</span>
                                                ) : r.upstream === "pending" ? (
                                                    <span className="text-amber-300 italic">awaiting agent</span>
                                                ) : r.upstream}
                                            </td>
                                            <td className="py-2 font-mono text-[11px] text-emerald-300">
                                                {assignedLb?.public_hostname || assignedLb?.ip_internal || "—"}
                                            </td>
                                            <td className="py-2 font-mono text-[11px] text-zinc-400">
                                                {target.dns_target ? `${target.dns_target_kind} → ${target.dns_target}` : "—"}
                                            </td>
                                            <td className="py-2 text-right">
                                                <button
                                                    data-testid={`caddy-route-delete-${r.host}`}
                                                    onClick={async () => {
                                                        if (!confirm(`Delete route ${r.host}?`)) return;
                                                        await api.delete(`/api/admin/lb/${lbs[0].id}/routes/${encodeURIComponent(r.host)}`);
                                                        mutateRoutes();
                                                    }}
                                                    className="cm-btn-ghost text-xs px-2 py-0.5 text-red-400">
                                                    <Trash2 size={11} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

// HA-Pairing: koppelt 2+ aktive Caddy-LBs zu einer Aktiv/Passiv-Gruppe mit
// gemeinsamer VIP (keepalived/VRRP). Zeigt bestehende Gruppen + Dissolve/
// Cert-Sync, oder ein Kopplungs-Formular (Master + Backup(s) + VIP).
function CaddyHaPanel({ lbs, haPairs, mutateHa, mutateVms, onDeploySecond }) {
    const groups = haPairs || [];
    const activeLbs = (lbs || []).filter((l) => l.status === "active");
    const [master, setMaster] = useState("");
    const [backups, setBackups] = useState([]); // vmid strings
    const [vip, setVip] = useState("");
    const [iface, setIface] = useState("eth0");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    // Sinnvolle Defaults: Master = erste aktive LB mit Hostname, Backups = Rest.
    useEffect(() => {
        if (master || groups.length > 0 || activeLbs.length < 2) return;
        const preferred = activeLbs.find((l) => l.public_hostname) || activeLbs[0];
        setMaster(String(preferred.vmid));
        setBackups(activeLbs.filter((l) => l.vmid !== preferred.vmid).map((l) => String(l.vmid)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLbs.length, groups.length]);

    const toggleBackup = (vmid) => {
        setBackups((b) => b.includes(vmid) ? b.filter((x) => x !== vmid) : [...b, vmid]);
    };

    const pair = async () => {
        setErr(null);
        if (!master) return setErr("Please select a master LB.");
        const bks = backups.filter((v) => v !== master);
        if (bks.length === 0) return setErr("Please select at least one backup LB.");
        if (!/^\d{1,3}(\.\d{1,3}){3}\/\d{1,2}$/.test(vip.trim()))
            return setErr("VIP im CIDR-Format angeben, z. B. 5.180.194.250/32");
        setBusy(true);
        try {
            const r = await api.post("/api/admin/infra/lb/ha/pair", {
                master_vmid: Number(master),
                backup_vmids: bks.map(Number),
                vip: vip.trim(),
                interface: iface.trim() || "eth0",
            });
            setVip("");
            mutateHa(); mutateVms();
            const inst = r.data?.install || [];
            const okCount = inst.filter((x) => x.ok).length;
            const failed = inst.filter((x) => !x.ok);
            if (failed.length === 0) {
                alert(`HA group created & keepalived installed/started on ${okCount} LB(s).\n` +
                      `Die VIP ${vip.trim()} bindet auf dem Master. Setze den DNS-A-Record der Tenants auf die VIP.`);
            } else {
                alert(`HA-Gruppe angelegt, ABER keepalived scheiterte auf ${failed.length} LB(s):\n` +
                      failed.map((f) => `• vmid ${f.vmid}: ${(f.detail || "").slice(0, 200)}`).join("\n") +
                      `\n\n(${okCount} successful.) Check VM reachability (qemu guest agent) and try again.`);
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setBusy(false); }
    };

    const disband = async (gid) => {
        if (!confirm("Dissolve the HA group? The LBs become standalone again " +
                     "(keine gemeinsame VIP, kein keepalived).")) return;
        try { await api.delete(`/api/admin/infra/lb/ha/pair/${gid}`); mutateHa(); mutateVms(); }
        catch (e) { alert(e?.response?.data?.detail || String(e)); }
    };

    const rsync = async (gid) => {
        try {
            const r = await api.post(`/api/admin/infra/lb/ha/pair/${gid}/rsync`);
            alert(`Cert sync (master → backup) started for ${r.data.queued} backup(s).`);
        } catch (e) { alert(e?.response?.data?.detail || String(e)); }
    };

    return (
        <div className="cm-card p-3 border border-zinc-800 bg-zinc-950" data-testid="lb-ha-card">
            <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={14} className="text-emerald-400" />
                <strong className="text-zinc-200 text-sm">High availability (active/passive, shared VIP)</strong>
            </div>

            {/* Bestehende HA-Gruppen */}
            {groups.map((g) => (
                <div key={g.group_id} className="border border-emerald-800/40 bg-emerald-900/10 p-3 mb-2 text-xs"
                     data-testid={`lb-ha-group-${g.group_id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-mono text-emerald-200">
                            VIP <span className="text-emerald-100">{g.vip}</span> · VRID {g.vrid} · IF {g.interface}
                        </div>
                        <div className="flex gap-2">
                            <button data-testid={`lb-ha-rsync-${g.group_id}`} onClick={() => rsync(g.group_id)}
                                className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1">
                                <RefreshCw size={11} /> Cert sync now
                            </button>
                            <button data-testid={`lb-ha-disband-${g.group_id}`} onClick={() => disband(g.group_id)}
                                className="cm-btn-ghost text-[11px] px-2 py-1 text-red-300 border border-red-800 flex items-center gap-1">
                                <Trash2 size={11} /> Dissolve
                            </button>
                        </div>
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 font-mono text-[11px]">
                        <div className="text-emerald-300">
                            MASTER: vmid {g.master?.vmid} · {g.master?.hostname || g.master?.ip_internal}
                        </div>
                        {(g.backups || []).map((b) => (
                            <div key={b.vmid} className="text-sky-300">
                                BACKUP: vmid {b.vmid} · {b.hostname || b.ip_internal}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Kopplungs-Formular / Hinweise */}
            {groups.length === 0 && activeLbs.length < 2 && (
                <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-zinc-400">
                    <span>
                        For real active/passive HA with a shared VIP you need{" "}
                        <strong className="text-zinc-200">mind. 2 aktive Caddy-LBs</strong>.
                        Aktuell: {activeLbs.length}. Deploy eine zweite LB (die bestehende bleibt unangetastet
                        and does not handle a fresh installation).
                    </span>
                    <button data-testid="lb-ha-add-second-btn" onClick={onDeploySecond}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 flex-shrink-0">
                        <Plus size={12} /> Add 2nd Caddy for HA
                    </button>
                </div>
            )}

            {groups.length === 0 && activeLbs.length >= 2 && (
                <div className="space-y-3 text-xs" data-testid="lb-ha-pair-form">
                    <div className="text-zinc-400">
                        Koppel deine bestehenden LBs zu einer HA-Gruppe — <strong className="text-zinc-200">ohne
                        Neu-Deploy</strong>. keepalived/VRRP wird per Agent ausgerollt, die VIP bindet auf dem Master.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Master (aktiv)</div>
                            <select value={master} onChange={(e) => { setMaster(e.target.value); setBackups((b) => b.filter((x) => x !== e.target.value)); }}
                                className="cm-input w-full text-[12px]" data-testid="lb-ha-master-select">
                                <option value="">— select —</option>
                                {activeLbs.map((l) => (
                                    <option key={l.vmid} value={String(l.vmid)}>
                                        vmid {l.vmid} · {l.public_hostname || l.ip_internal}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Backup(s) (passiv)</div>
                            <div className="space-y-1 max-h-28 overflow-auto border border-zinc-800 p-2">
                                {activeLbs.filter((l) => String(l.vmid) !== master).map((l) => (
                                    <label key={l.vmid} className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={backups.includes(String(l.vmid))}
                                            onChange={() => toggleBackup(String(l.vmid))}
                                            data-testid={`lb-ha-backup-${l.vmid}`} />
                                        <span className="font-mono">vmid {l.vmid} · {l.public_hostname || l.ip_internal}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Gemeinsame VIP (CIDR)</div>
                            <input type="text" value={vip} onChange={(e) => setVip(e.target.value)}
                                placeholder="z. B. 5.180.194.250/32"
                                className="cm-input w-full text-[12px] font-mono" data-testid="lb-ha-vip-input" />
                            <div className="text-[11px] text-zinc-500 mt-1">
                                Free public IP; tenant DNS will point to this IP.
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Public-Interface (auto)</div>
                            <input type="text" value={iface} onChange={(e) => setIface(e.target.value)}
                                placeholder="auto-detect" className="cm-input w-full text-[12px] font-mono"
                                data-testid="lb-ha-iface-input" />
                            <div className="text-[11px] text-zinc-500 mt-1">
                                Auto-detected in the guest from the public IP — override only if needed.
                            </div>
                        </div>
                    </div>
                    {err && <div className="text-red-400 text-xs" data-testid="lb-ha-error">{err}</div>}
                    <div className="flex justify-end">
                        <button onClick={pair} disabled={busy} data-testid="lb-ha-pair-btn"
                            className="cm-btn-primary text-xs px-4 py-2 flex items-center gap-1 disabled:opacity-50">
                            <ShieldCheck size={12} /> {busy ? "Koppeln…" : "Als HA-Cluster koppeln"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


function CaddyLbCard({ lb, haInfo, routeCount, probing, onProbe, onDelete, onRebuild, onHostnameSaved }) {
    const [editing, setEditing] = useState(false);
    const [hostname, setHostname] = useState(lb.public_hostname || "");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const ready = lb.status === "active" && lb.public_hostname;
    const save = async () => {
        setSaving(true); setErr(null);
        try {
            await api.patch(`/api/admin/infra/service-vms/${lb.id}`, {
                public_hostname: hostname.trim(),
            });
            setEditing(false);
            onHostnameSaved && onHostnameSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setSaving(false); }
    };
    return (
        <div className={`cm-card p-4 ${ready ? "" : "border border-amber-700/40"}`} data-testid={`caddy-lb-${lb.id}`}>
            <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                    <div className="cm-h3">{lb.hostname || `LB ${lb.vmid}`}</div>
                    <div className="font-mono text-xs text-zinc-500">
                        Public {lb.ip_internal} · vmid {lb.vmid} · node {lb.node}
                    </div>
                </div>
                <StatusBadge status={lb.status} />
            </div>

            {haInfo && (
                <div
                    className={`flex items-center gap-2 -mt-1 mb-2 text-[11px] font-mono px-2 py-1 border ${
                        haInfo.role === "master"
                            ? "border-emerald-700/50 bg-emerald-900/20 text-emerald-300"
                            : "border-sky-700/50 bg-sky-900/20 text-sky-300"
                    }`}
                    data-testid={`caddy-lb-ha-badge-${lb.id}`}
                >
                    <ShieldCheck size={11} />
                    HA {haInfo.role === "master" ? "MASTER" : "BACKUP"} · VIP {haInfo.vip}
                </div>
            )}

            {/* Public DNS hostname (operator-set) */}
            <div className="mt-3 border-t border-zinc-800 pt-3">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
                    Public DNS Name (tenant CNAME target)
                </div>
                {!editing ? (
                    <div className="flex items-center justify-between gap-2">
                        <code className={`text-[12px] font-mono truncate ${lb.public_hostname ? "text-emerald-300" : "text-amber-400"}`}>
                            {lb.public_hostname || "⚠ not set — LB cannot accept tenants"}
                        </code>
                        <button
                            onClick={() => { setHostname(lb.public_hostname || ""); setEditing(true); }}
                            data-testid={`caddy-lb-edit-hostname-${lb.id}`}
                            className="cm-btn-ghost text-[11px] px-2 py-1 flex-shrink-0"
                        >
                            {lb.public_hostname ? "Edit" : "Set hostname"}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <input
                            type="text"
                            value={hostname}
                            onChange={(e) => setHostname(e.target.value)}
                            placeholder="e.g. lb1.cm.host-on.de"
                            className="cm-input w-full text-[12px] font-mono"
                            data-testid={`caddy-lb-hostname-input-${lb.id}`}
                            autoFocus
                        />
                        <div className="text-[11px] text-zinc-500">
                            Set the public DNS A-record <code>{hostname || "lb1.cm.host-on.de"} → {lb.ip_internal}</code> in
                            your DNS provider BEFORE saving. Tenants will be told to CNAME their domains here.
                        </div>
                        {err && <div className="text-red-400 text-xs">{err}</div>}
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => { setEditing(false); setErr(null); }} className="cm-btn-ghost text-[11px] px-2 py-1">Cancel</button>
                            <button onClick={save} disabled={saving} data-testid={`caddy-lb-save-hostname-${lb.id}`} className="cm-btn-primary text-[11px] px-2 py-1 disabled:opacity-50">
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-zinc-950 border border-zinc-800 p-2">
                    <div className="text-zinc-500 text-[10px] uppercase">Routes assigned</div>
                    <div className="font-mono text-zinc-200" data-testid={`caddy-lb-routes-${lb.id}`}>{routeCount}</div>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 p-2">
                    <div className="text-zinc-500 text-[10px] uppercase">Ready for tenants</div>
                    <div className={`font-mono ${ready ? "text-emerald-300" : "text-amber-400"}`}>
                        {ready ? "✓ yes" : (lb.status !== "active" ? "no — not active" : "no — hostname unset")}
                    </div>
                </div>
            </div>

            <div className="flex gap-2 mt-3">
                <button
                    onClick={onProbe}
                    disabled={probing}
                    data-testid={`caddy-lb-probe-${lb.id}`}
                    className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-50"
                >
                    <Activity size={12} /> {probing ? "Probing…" : "Health Check"}
                </button>
                <button
                    onClick={onRebuild}
                    data-testid={`caddy-lb-rebuild-${lb.id}`}
                    title="Safely rebuild: fresh VM + route rehydrate, then delete the old one"
                    className="cm-btn-ghost text-xs px-2 py-1 text-sky-300 flex items-center gap-1"
                >
                    <RefreshCw size={12} /> Neu aufbauen
                </button>
                <button onClick={onDelete} data-testid={`caddy-lb-delete-${lb.id}`} className="cm-btn-ghost text-xs px-2 py-1 text-red-400 flex items-center gap-1">
                    <Trash2 size={12} /> Destroy
                </button>
            </div>
        </div>
    );
}

function CaddyDeployForm({ clusters, publicPools, haCluster, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        host_network_id: publicPools[0]?.id || "",
        ip_override: "",      // "" = auto-allocate
        ip_override_2: "",    // 2nd LB in HA mode
        cpu: 1, ram_mb: 1024, disk_gb: 10,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [progress, setProgress] = useState(null);

    // Free-IP fetcher — only when a pool is selected. Re-fetches when
    // pool changes so the user always sees a current snapshot.
    const { data: freeIps } = useSWR(
        form.host_network_id
            ? `/api/admin/infra/host-networks/${form.host_network_id}/free-ips`
            : null,
        fetcher,
    );

    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setBusy(true);
        try {
            const n = haCluster ? 2 : 1;
            const ips = haCluster
                ? [form.ip_override, form.ip_override_2]
                : [form.ip_override];
            for (let i = 0; i < n; i++) {
                setProgress(`Queueing Caddy LB ${i + 1}/${n}${ips[i] ? ` on ${ips[i]}` : ""}…`);
                await api.post("/api/admin/infra/service-vms", {
                    cluster_id: form.cluster_id,
                    kind: "caddy",
                    host_network_id: form.host_network_id,
                    ip_override: ips[i] || null,
                    cpu: form.cpu, ram_mb: form.ram_mb, disk_gb: form.disk_gb,
                });
            }
            setProgress(`✓ ${n} Caddy LB${n > 1 ? "s" : ""} queued. Watch the list below — should appear within ~5s, status flips to "active" after ~3min.`);
            setTimeout(onClose, 2500);
        } catch (e) {
            const raw = e?.response?.data?.detail ?? e?.message ?? e;
            setErr(typeof raw === "string" ? raw : JSON.stringify(raw));
            setProgress(null);
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2 border border-emerald-700" data-testid="caddy-deploy-form">
            <div className="cm-h3 mb-2">
                {haCluster ? "Deploy Caddy LB Cluster (2 VMs, HA)" : "Deploy Caddy LB"}
            </div>
            <div className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 p-2 mb-2">
                Each LB gets 3 NICs: <strong>eth0</strong> on the public LAN (Internet),
                <strong> eth1</strong> in the service-zone (talks to Redis/Collabora/tenants),
                <strong> eth2</strong> reserved in the lb-zone (future shared cert-storage).
                Admin API on 127.0.0.1:2019 — CM hits it via guest-exec.
                Auto-TLS via Let&apos;s Encrypt for any registered route.
                <span className="block mt-1 text-emerald-300">
                    ⏱️ Single-LB deploy: <strong>~1min</strong> (apt install caddy)
                    until status flips to &quot;active&quot;.
                </span>
                {haCluster && (
                    <span className="block mt-1 text-amber-300">
                        ⚠️ HA mode (2+ LBs) currently deploys identical Caddys but
                        without shared cert storage — each VM holds its own Let&apos;s
                        Encrypt cert, doubling the rate-limit consumption. Use only
                        if your DNS round-robins between 2 IPs AND you accept this
                        trade-off. Proper HA with redis-shared certs is the next
                        iteration.
                    </span>
                )}
            </div>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })} className="cm-input w-full" data-testid="caddy-deploy-cluster">
                    {(clusters || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </label>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Public-IP Pool (eth0)</span>
                <select
                    value={form.host_network_id}
                    onChange={(e) => setForm({ ...form, host_network_id: e.target.value, ip_override: "", ip_override_2: "" })}
                    className="cm-input w-full" data-testid="caddy-deploy-pool"
                >
                    {publicPools.length === 0 && <option value="">— no public pool defined —</option>}
                    {publicPools.map((p) => {
                        const optLabel = `${p.name} — ${p.subnet}${p.bridge ? `  →  ${p.bridge}` : ""}`;
                        return <option key={p.id} value={p.id}>{optLabel}</option>;
                    })}
                </select>
                {(() => {
                    const selected = publicPools.find((p) => p.id === form.host_network_id);
                    if (!selected) return null;
                    return (
                        <div className="mt-1 text-[11px] font-mono text-zinc-500">
                            eth0 → bridge <span className="text-emerald-300">{selected.bridge || "vmbr0 (default)"}</span>
                        </div>
                    );
                })()}
            </label>

            {/* IP-pinning picker — operator-friendly */}
            {freeIps && (
                <div className="bg-zinc-950 border border-zinc-800 p-2 text-xs">
                    <div className="text-zinc-400 mb-1">
                        Pin specific IP{haCluster ? "s" : ""} (optional · {freeIps.free?.length || 0} free
                        of {(freeIps.free?.length || 0) + (freeIps.used?.length || 0)}):
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="block text-[11px] text-zinc-500 uppercase mb-1">
                                {haCluster ? "LB #1 IP" : "Public IP"}
                            </span>
                            <select
                                value={form.ip_override}
                                onChange={(e) => setForm({ ...form, ip_override: e.target.value })}
                                className="cm-input w-full" data-testid="caddy-deploy-ip"
                            >
                                <option value="">Auto-allocate (next free)</option>
                                {(freeIps.free || []).filter((ip) => ip !== form.ip_override_2).map((ip) => (
                                    <option key={ip} value={ip}>{ip}</option>
                                ))}
                            </select>
                        </label>
                        {haCluster && (
                            <label className="block">
                                <span className="block text-[11px] text-zinc-500 uppercase mb-1">LB #2 IP</span>
                                <select
                                    value={form.ip_override_2}
                                    onChange={(e) => setForm({ ...form, ip_override_2: e.target.value })}
                                    className="cm-input w-full" data-testid="caddy-deploy-ip2"
                                >
                                    <option value="">Auto-allocate (next free)</option>
                                    {(freeIps.free || []).filter((ip) => ip !== form.ip_override).map((ip) => (
                                        <option key={ip} value={ip}>{ip}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    {(freeIps.used || []).length > 0 && (
                        <details className="mt-2">
                            <summary className="text-zinc-500 cursor-pointer text-[11px]">
                                {freeIps.used.length} IPs already in use — click to see who has them
                            </summary>
                            <ul className="mt-1 ml-3 list-disc text-zinc-500 text-[11px] font-mono">
                                {freeIps.used.map((u) => (
                                    <li key={u.ip}>{u.ip} → {u.by}</li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}

            <div className="grid grid-cols-3 gap-2">
                <Field label="CPU cores" type="number" min={1} v={form.cpu} on={(v) => setForm({ ...form, cpu: parseInt(v, 10) || 1 })} testid="caddy-deploy-cpu" />
                <Field label="RAM (MB)" type="number" min={512} v={form.ram_mb} on={(v) => setForm({ ...form, ram_mb: parseInt(v, 10) || 1024 })} testid="caddy-deploy-ram" />
                <Field label="Disk (GB)" type="number" min={5} v={form.disk_gb} on={(v) => setForm({ ...form, disk_gb: parseInt(v, 10) || 10 })} testid="caddy-deploy-disk" />
            </div>
            {progress && <div className="text-emerald-300 text-xs font-mono">⏳ {progress}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="caddy-deploy-error">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id || !form.host_network_id} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="caddy-deploy-submit">
                    {busy ? "Provisioning…" : haCluster ? "Deploy Cluster" : "Deploy LB"}
                </button>
            </div>
        </form>
    );
}

function CaddyAddRouteForm({ lb, onClose }) {
    const [form, setForm] = useState({
        host: "",
        upstream: "",
        mode: "proxy",          // "proxy" | "placeholder"
        skip_dns_check: false,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [success, setSuccess] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setBusy(true);
        try {
            const payload = {
                host: form.host,
                skip_dns_check: form.skip_dns_check,
                ...(form.mode === "placeholder"
                    ? { placeholder: true }
                    : { upstream: form.upstream }),
            };
            const r = await api.post(`/api/admin/lb/${lb.id}/routes`, payload);
            const target = form.mode === "placeholder"
                ? "splash page"
                : form.upstream;
            setSuccess(`✓ Pushed to ${r.data.pushed.length} LB(s) · ${target} · DNS: ${r.data.dns_check || "skipped"}`);
            setTimeout(onClose, 1500);
        } catch (e) {
            const raw = e?.response?.data?.detail ?? e?.message ?? e;
            setErr(typeof raw === "string" ? raw : JSON.stringify(raw));
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="bg-zinc-950 border border-emerald-700 p-3 mb-3 space-y-2" data-testid="caddy-route-form">
            <div className="text-xs text-zinc-400 mb-1">
                Adding a route — Caddy will obtain a Let&apos;s Encrypt cert for <code>host</code>
                on first request. <strong>BYOD DNS check</strong>: <code>host</code> must
                already resolve (A or CNAME) to one of the LB public IPs. Otherwise
                tick &quot;Skip DNS check&quot; (use cautiously).
            </div>

            {/* Mode picker */}
            <div className="grid grid-cols-2 gap-2 my-2" role="radiogroup" aria-label="Route mode">
                <label
                    className={`cursor-pointer p-2 border ${form.mode === "proxy" ? "border-emerald-500 bg-emerald-950/30" : "border-zinc-700 bg-zinc-900"}`}
                    data-testid="route-mode-proxy"
                >
                    <input
                        type="radio" name="mode" value="proxy" className="sr-only"
                        checked={form.mode === "proxy"}
                        onChange={() => setForm({ ...form, mode: "proxy" })}
                    />
                    <div className="text-xs text-zinc-200 font-semibold">Reverse Proxy</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                        Forward to a backend upstream (e.g. Nextcloud, Collabora)
                    </div>
                </label>
                <label
                    className={`cursor-pointer p-2 border ${form.mode === "placeholder" ? "border-emerald-500 bg-emerald-950/30" : "border-zinc-700 bg-zinc-900"}`}
                    data-testid="route-mode-placeholder"
                >
                    <input
                        type="radio" name="mode" value="placeholder" className="sr-only"
                        checked={form.mode === "placeholder"}
                        onChange={() => setForm({ ...form, mode: "placeholder" })}
                    />
                    <div className="text-xs text-zinc-200 font-semibold">Placeholder Page</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                        Whitelabel &ldquo;your cloud is being prepared&rdquo; splash
                        <button
                            type="button"
                            data-testid="route-placeholder-preview"
                            onClick={(e) => {
                                e.preventDefault();
                                window.open("/api/admin/lb/placeholder-preview", "_blank", "width=900,height=700");
                            }}
                            className="ml-1 underline text-emerald-400 hover:text-emerald-300"
                        >
                            preview ↗
                        </button>
                    </div>
                </label>
            </div>

            <Field label="Hostname (e.g. cloud.kunde.de)" v={form.host} on={(v) => setForm({ ...form, host: v.trim() })} testid="route-host" />
            {form.mode === "proxy" && (
                <Field label="Upstream (e.g. 10.16.0.11:9980)" v={form.upstream} on={(v) => setForm({ ...form, upstream: v.trim() })} testid="route-upstream" />
            )}
            <label className="flex items-center gap-2 text-xs text-zinc-400">
                <input type="checkbox" checked={form.skip_dns_check} onChange={(e) => setForm({ ...form, skip_dns_check: e.target.checked })} data-testid="route-skip-dns" />
                Skip DNS check (only for internal/setup hostnames)
            </label>
            {success && <div className="text-emerald-300 text-xs font-mono">{success}</div>}
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="caddy-route-error">{err}</div>}
            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button
                    type="submit"
                    disabled={busy || !form.host || (form.mode === "proxy" && !form.upstream)}
                    className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                    data-testid="caddy-route-submit"
                >
                    {busy ? "Pushing…" : form.mode === "placeholder" ? "Add Placeholder Route" : "Add Route"}
                </button>
            </div>
        </form>
    );
}

// =====================================================================
// Loadbalancers — external/legacy Caddy registrations (admin URL based)
// =====================================================================
export function LoadbalancerPanel() {
    const { data, error, mutate } = useSWR("/api/admin/infra/loadbalancers", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [tests, setTests] = useState({});

    const onTest = async (id) => {
        setTests((t) => ({ ...t, [id]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/loadbalancers/${id}/test`);
            setTests((t) => ({ ...t, [id]: r.data }));
        } catch (e) {
            setTests((t) => ({ ...t, [id]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };
    const onDelete = async (id) => {
        if (!confirm("Delete this loadbalancer?")) return;
        await api.delete(`/api/admin/infra/loadbalancers/${id}`);
        mutate();
    };
    if (!data && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button data-testid="lb-add-btn" onClick={() => setShowForm(true)} className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1">
                    <Plus size={12} /> Add loadbalancer
                </button>
            </div>
            {showForm && <LoadbalancerForm onClose={() => { setShowForm(false); mutate(); }} />}
            {data?.length === 0 ? (
                <Empty title="No loadbalancer yet" hint="Register a Caddy admin endpoint." />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {data.map((lb) => (
                        <div key={lb.id} className="cm-card p-4" data-testid={`lb-card-${lb.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="cm-h3">{lb.name}</div>
                                    <div className="font-mono text-xs text-zinc-500">{lb.provider} · public {lb.public_ip}</div>
                                </div>
                                <StatusBadge status={lb.status} />
                            </div>
                            <KV label="Admin" v={lb.admin_endpoint} />
                            <KV label="Token" v={lb.admin_token_masked || "—"} />
                            <div className="flex gap-2 mt-3">
                                <button data-testid={`lb-test-${lb.id}`} onClick={() => onTest(lb.id)} className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1">
                                    <FlaskConical size={12} /> Test
                                </button>
                                <button data-testid={`lb-delete-${lb.id}`} onClick={() => onDelete(lb.id)} className="cm-btn-ghost text-xs px-2 py-1 text-red-400 flex items-center gap-1">
                                    <Trash2 size={12} /> Delete
                                </button>
                            </div>
                            {tests[lb.id] && (
                                <div className={`mt-2 text-xs font-mono p-2 ${tests[lb.id].ok ? "bg-emerald-950 text-emerald-300" : "bg-red-950 text-red-300"}`}>
                                    {tests[lb.id].loading ? "Testing…" : tests[lb.id].ok ? `OK · keys ${JSON.stringify(tests[lb.id].info?.info?.config_keys || [])}` : `FAIL: ${tests[lb.id].error}`}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function LoadbalancerForm({ onClose }) {
    const [form, setForm] = useState({ name: "", provider: "caddy", admin_endpoint: "http://lb.example.com:2019", admin_token: "", public_ip: "" });
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try { await api.post("/api/admin/infra/loadbalancers", form); onClose(); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid="lb-form">
            <div className="cm-h3 mb-2">New loadbalancer</div>
            <Field label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="lb-name" />
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Provider</span>
                <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="cm-input w-full" data-testid="lb-provider">
                    <option value="caddy">Caddy (admin API)</option>
                    <option value="nginx" disabled>Nginx (later)</option>
                </select>
            </label>
            <Field label="Admin endpoint" v={form.admin_endpoint} on={(v) => setForm({ ...form, admin_endpoint: v })} testid="lb-endpoint" />
            <Field label="Admin token (optional)" type="password" v={form.admin_token} on={(v) => setForm({ ...form, admin_token: v })} testid="lb-token" />
            <Field label="Public IP" v={form.public_ip} on={(v) => setForm({ ...form, public_ip: v })} testid="lb-public-ip" />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <FormButtons onClose={onClose} testid="lb" />
        </form>
    );
}

// =====================================================================
// Shared bits
// =====================================================================
function KV({ label, v }) {
    return (
        <div className="flex justify-between text-xs py-0.5">
            <span className="text-zinc-500 uppercase tracking-wider">{label}</span>
            <span className="font-mono text-zinc-300 truncate ml-2">{v ?? "—"}</span>
        </div>
    );
}

function FormButtons({ onClose, testid }) {
    return (
        <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
            <button type="submit" className="cm-btn-primary text-xs px-3 py-1.5" data-testid={`${testid}-submit`}>Save</button>
        </div>
    );
}

/**
 * Generic "new pool" form used by Redis + Collabora — the fields
 * (name/region/type/max_clouds) are identical across both.
 */
function PoolForm({ apiBase, testidPrefix, title, typeOptions, defaults, onClose }) {
    const [form, setForm] = useState(defaults);
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try { await api.post(apiBase, form); onClose(); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
    };
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-2" data-testid={`${testidPrefix}-pool-form`}>
            <div className="cm-h3 mb-2">{title}</div>
            <Field label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid={`${testidPrefix}p-name`} />
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Pool type</span>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="cm-input w-full" data-testid={`${testidPrefix}p-type`}>
                    {typeOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
            </label>
            <Field label="Max clouds" type="number" v={form.max_clouds} on={(v) => setForm({ ...form, max_clouds: parseInt(v, 10) || 0 })} testid={`${testidPrefix}p-max`} />
            {err && <div className="text-red-400 text-xs">{err}</div>}
            <FormButtons onClose={onClose} testid={`${testidPrefix}p`} />
        </form>
    );
}

/**
 * Generic pool card — header + members + "Add member" + "Delete pool" +
 * optional "Provision VM" (when `provisionKind` is set). The "Provision VM"
 * flow asks for CPU/RAM/Disk, calls /api/admin/infra/service-vms with the
 * pool_id stamped in, so the new VM auto-registers as a member.
 */
function PoolCard({ pool, apiBase, metaLine, onMutate, onAddMember, memberDisplay,
                   memberTests, onTestMember, addMemberLabel, testidPrefix,
                   provisionKind }) {
    const [showProvision, setShowProvision] = useState(false);
    const onDeletePool = async () => {
        if (!confirm("Delete this pool?")) return;
        try { await api.delete(`${apiBase}/${pool.id}`); onMutate(); }
        catch (e) { alert(e?.response?.data?.detail || String(e)); }
    };
    const onDeleteMember = async (mid) => {
        if (!confirm("Remove this member?")) return;
        await api.delete(`${apiBase}/${pool.id}/members/${mid}`);
        onMutate();
    };
    return (
        <div className="cm-card p-4" data-testid={`${testidPrefix}-pool-${pool.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                    <div className="cm-h3">{pool.name}</div>
                    <div className="font-mono text-xs text-zinc-500">{metaLine}</div>
                </div>
                <StatusBadge status={pool.status} />
            </div>
            <div className="space-y-2 mt-2">
                {(pool.members || []).length === 0 ? (
                    <div className="text-xs text-zinc-500">No members yet.</div>
                ) : (
                    pool.members.map((m) => (
                        <div key={m.id} className="bg-zinc-950 border border-zinc-800 p-2 text-xs flex items-center justify-between" data-testid={`${testidPrefix}-member-${m.id}`}>
                            <div className="font-mono text-zinc-200 truncate">
                                <span className="mr-2 px-1.5 py-0.5 text-[10px] uppercase bg-zinc-800 text-zinc-300">{m.node_name || "node"}</span>
                                {m.managed_by === "cm" && (
                                    <span className="mr-2 px-1.5 py-0.5 text-[10px] uppercase bg-[#CCFF00] text-[#05070A]">cm-vm</span>
                                )}
                                {memberDisplay(m)}
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button onClick={() => onTestMember(pool.id, m.id)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5" data-testid={`${testidPrefix}-member-test-${m.id}`}>
                                    <FlaskConical size={10} />
                                </button>
                                <button onClick={() => onDeleteMember(m.id)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5 text-red-400" data-testid={`${testidPrefix}-member-delete-${m.id}`}>
                                    <Trash2 size={10} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
                {Object.entries(memberTests || {}).filter(([id]) => (pool.members || []).some((m) => m.id === id)).map(([id, r]) => (
                    <div key={`t-${id}`} className={`text-[11px] font-mono pl-2 ${r.ok ? "text-emerald-300" : "text-red-300"}`}>
                        {r.loading ? "Testing…" : r.ok ? `OK · ${r.version || JSON.stringify(r.info).slice(0, 60)}…` : `FAIL: ${r.error}`}
                    </div>
                ))}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
                {onAddMember && (
                    <button
                        onClick={onAddMember}
                        disabled={pool.type === "standalone" && (pool.members || []).length >= 1}
                        className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-40"
                        data-testid={`${testidPrefix}-pool-add-member-${pool.id}`}
                    >
                        <Plus size={12} /> {addMemberLabel}
                    </button>
                )}
                {provisionKind && (
                    <button
                        onClick={() => setShowProvision(true)}
                        disabled={pool.type === "standalone" && (pool.members || []).length >= 1}
                        className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-40"
                        data-testid={`${testidPrefix}-pool-provision-${pool.id}`}
                        title="Auto-create a VM on a control-plane node, install the service via cloud-init, register it as a pool member"
                    >
                        <Plus size={12} /> Provision VM
                    </button>
                )}
                <button onClick={onDeletePool} className="cm-btn-ghost text-xs px-3 py-1.5 text-red-400 flex items-center gap-1" data-testid={`${testidPrefix}-pool-delete-${pool.id}`}>
                    <Trash2 size={12} /> Delete pool
                </button>
            </div>
            {showProvision && provisionKind && (
                <ProvisionVmForm
                    kind={provisionKind}
                    pool={pool}
                    onClose={() => { setShowProvision(false); onMutate(); }}
                />
            )}
        </div>
    );
}

/**
 * 1-Step "Add X VM" flow — creates an auto-managed pool if missing,
 * then provisions the service VM directly on a control-plane node.
 *
 * The Pool-Concept stays around for advanced use cases (external endpoints),
 * but the primary path now skips the manual pool-creation step entirely.
 */
function AddServiceVmFlow({ kind, onClose, presetPoolId = null }) {
    const apiBase = kind === "redis"
        ? "/api/admin/infra/redis-pools"
        : kind === "collabora"
        ? "/api/admin/infra/collabora-pools"
        : kind === "nextcloud-eurooffice"
        ? "/api/admin/infra/eurooffice-pools"
        : kind === "notify_push"
        ? "/api/admin/infra/notify-push-pools"
        : (kind === "talk-signaling" || kind === "coturn" || kind === "talk-recording")
        ? "/api/admin/infra/talk-hpb-pools"
        : "/api/admin/infra/loadbalancers";
    const { data: existingPools } = useSWR(apiBase, fetcher);
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const { data: ipPoolsRaw } = useSWR("/api/admin/infra/host-networks", fetcher);

    // Phase 2: filter pools by purpose. Redis/Collabora/notify_push/talk-signaling
    // live in a service-zone (SDN overlay, no public IP). Caddy + coturn live on
    // the public-IP pool (TURN needs public IPv4 to be reachable by NC clients).
    const wantedPurpose = (kind === "caddy" || kind === "coturn") ? "public" : "service";
    const ipPools = (ipPoolsRaw || []).filter(
        (p) => (p.purpose || "public") === wantedPurpose,
    );

    const defaults = {
        redis:           { cpu: 1, ram_mb: 2048, disk_gb: 10, max_clouds: 500 },
        collabora:       { cpu: 2, ram_mb: 4096, disk_gb: 20, max_clouds: 200 },
        "nextcloud-eurooffice": { cpu: 4, ram_mb: 8192, disk_gb: 60, max_clouds: 200 },
        notify_push:     { cpu: 1, ram_mb: 1024, disk_gb: 10, max_clouds: 500 },
        "talk-signaling":{ cpu: 2, ram_mb: 4096, disk_gb: 20, max_clouds: 200 },
        coturn:          { cpu: 1, ram_mb: 1024, disk_gb: 10, max_clouds: 200 },
        "talk-recording":{ cpu: 4, ram_mb: 4096, disk_gb: 30, max_clouds: 200 },
        caddy:           { cpu: 1, ram_mb: 1024, disk_gb: 10, max_clouds: 0 },
    }[kind] || { cpu: 1, ram_mb: 1024, disk_gb: 10, max_clouds: 100 };

    const [form, setForm] = useState({
        cluster_id: null,
        host_network_id: null,
        pool_name: presetPoolId ? null : "default",
        pool_id: presetPoolId,
        ...defaults,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [progress, setProgress] = useState(null);

    const effectiveClusterId = form.cluster_id ?? clusters?.[0]?.id ?? "";
    const activeCluster = (clusters || []).find((c) => c.id === effectiveClusterId);
    // The cluster's default `host_network_id` may point to a pool of the
    // WRONG purpose (e.g. cluster default is a public pool, but Redis
    // needs a service pool). Only honour the default if it matches the filter.
    const clusterDefaultPoolId =
        (ipPools || []).some((p) => p.id === activeCluster?.host_network_id)
            ? activeCluster.host_network_id
            : null;
    const effectivePoolId = form.host_network_id ?? clusterDefaultPoolId ?? "";

    const { data: preview } = useSWR(
        effectivePoolId ? `/api/admin/infra/host-networks/${effectivePoolId}/next-ip` : null,
        fetcher,
        { refreshInterval: 5000 },
    );

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            // -----------------------------------------------------------
            // Validate EVERYTHING up-front BEFORE any side effect.
            // Earlier versions created the pool first → if the IP-pool
            // selection failed afterwards we leaked an orphan empty pool
            // (this is what made operators see "Pool 0/500 but no VM").
            // -----------------------------------------------------------
            if (!effectiveClusterId) {
                throw new Error("No cluster selected. Pick one in the dropdown above.");
            }
            if (!effectivePoolId) {
                throw new Error(
                    wantedPurpose === "service"
                        ? "No service-zone IP pool selected. Bootstrap the SDN service-zone first (Infrastructure → IP Pools → Bootstrap Service Zone)."
                        : "No public IP pool selected. Pick one in the dropdown above.",
                );
            }
            // Sanity check: the user might have ALREADY a pool with name
            // = form.pool_name (e.g. left-over empty pool from a previous
            // crash). We reuse it transparently so we don't error 409
            // and confuse the operator.
            setProgress("Looking up / creating storage pool…");
            let pool = null;
            if (form.pool_id) {
                // Preset by parent (e.g. "add VM to *this* pool" flow)
                pool = (existingPools || []).find((p) => p.id === form.pool_id) || { id: form.pool_id };
            } else if (kind === "notify_push") {
                // 1 VM = 1 pool: every notify_push VM gets its OWN fresh
                // standalone pool — never reuse an existing one. Add capacity
                // by adding more VMs (each lands in its own pool).
                const uniqueName = `np-${Math.random().toString(16).slice(2, 8)}`;
                const r = await api.post(apiBase, {
                    name: uniqueName,
                    region: "default",
                    type: "standalone",
                    max_clouds: form.max_clouds,
                });
                pool = r.data;
            } else {
                pool = (existingPools || []).find((p) => p.name === form.pool_name);
            }
            if (!pool) {
                if (kind === "redis" || kind === "collabora" || kind === "nextcloud-eurooffice") {
                    const r = await api.post(apiBase, {
                        name: form.pool_name,
                        region: "default",
                        type: "standalone",
                        max_clouds: form.max_clouds,
                    });
                    pool = r.data;
                } else if (kind === "talk-signaling" || kind === "coturn") {
                    const r = await api.post(apiBase, {
                        name: form.pool_name,
                        region: "default",
                        type: "cluster",
                        max_clouds: form.max_clouds,
                    });
                    pool = r.data;
                } else if (kind === "caddy") {
                    // Loadbalancer rows don't have a "pool" concept — the
                    // service VM IS the loadbalancer. Skip pool creation.
                    pool = { id: null };
                }
            }

            // Provision the VM (auto-registers as member on success).
            //
            // ALWAYS send the effective (filter-validated) pool ID, never
            // let the backend silently fall back to the cluster default —
            // that's how Redis ended up on the public LAN once.
            setProgress("Provisioning VM on control-plane node…");
            await api.post("/api/admin/infra/service-vms", {
                cluster_id: effectiveClusterId,
                kind,
                pool_id: pool?.id,
                host_network_id: effectivePoolId,
                cpu: form.cpu,
                ram_mb: form.ram_mb,
                disk_gb: form.disk_gb,
            });
            setProgress(
                "Queued — VM is being cloned in Proxmox. Watch the in-flight "
                + "list below; the row turns green when cloud-init finishes (~60-180s).",
            );
            setTimeout(onClose, 2000);
        } catch (e) {
            const raw = e?.response?.data?.detail ?? e?.message ?? e;
            setErr(typeof raw === "string" ? raw : JSON.stringify(raw));
            setProgress(null);
        } finally {
            setBusy(false);
        }
    };

    const label = kind === "redis" ? "Redis"
                  : kind === "collabora" ? "Collabora"
                  : kind === "nextcloud-eurooffice" ? "Euro-Office"
                  : kind === "notify_push" ? "Notify Push"
                  : kind === "talk-signaling" ? "Talk Signaling"
                  : kind === "coturn" ? "TURN/coTURN"
                  : kind === "talk-recording" ? "Talk Recording"
                  : "Caddy LB";
    const poolLabel = wantedPurpose === "service"
        ? "Service-Zone Subnet (interner SDN-Overlay — keine Public IP)"
        : "Public-IP Pool (Internet-facing)";
    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-3 border border-[#CCFF00]" data-testid={`add-svc-vm-${kind}`}>
            <div className="cm-h3">Add {label} VM — a new VM with its own capacity</div>
            <div className="text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 p-2">
                Es wird <strong>eine</strong> Debian-13 VM auf einem Control-Plane-Node geklont,
                gets the next free IP from the service-zone subnet assigned and installs{" "}
                <span className="text-zinc-300">{label}</span> via cloud-init. Eine eigene
                Standalone-Pool wird automatisch angelegt — Tenants verteilen sich darauf bis das
                <code className="mx-1">max_clouds</code> limit is reached. For more capacity:
                einfach eine weitere VM via &quot;Add {label} VM&quot; deployen. Gesamtdauer: 60–180s.
                {wantedPurpose === "service" && (
                    <div className="mt-1 text-zinc-400">
                        ℹ️ <strong>{label}</strong> runs in the internal service-zone overlay (no public IP).
                        Tenants reach the service via the egress GW / Caddy LB, never directly.
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                        Cluster {(clusters || []).length > 1 ? `(${(clusters || []).length} available)` : ""}
                    </span>
                    <select value={effectiveClusterId}
                             onChange={(e) => setForm({ ...form, cluster_id: e.target.value, host_network_id: null })}
                             className="cm-input w-full" data-testid={`asv-${kind}-cluster`}>
                        {(clusters || []).map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}{c.status === "active" ? "  ·  default-active" : ""}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                        {poolLabel} {form.host_network_id ? "(override)" : "(cluster default)"}
                    </span>
                    <select value={effectivePoolId}
                             onChange={(e) => setForm({ ...form, host_network_id: e.target.value })}
                             className="cm-input w-full" data-testid={`asv-${kind}-pool`}>
                        <option value="">— pick a pool —</option>
                        {(ipPools || []).map((p) => {
                            const optLabel = `${p.name} — ${p.subnet}`;
                            return <option key={p.id} value={p.id}>{optLabel}</option>;
                        })}
                    </select>
                </label>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 p-2 text-[11px] font-mono">
                {ipPools.length === 0 && (
                    <span className="text-amber-400">
                        ⚠ No <strong>{wantedPurpose}</strong>-purpose IP pools defined.{" "}
                        {wantedPurpose === "service" ? (
                            <>This service must run inside the SDN service zone.
                            Go to <strong>IP Pools → Bootstrap Service Zone</strong> first.</>
                        ) : (
                            <>Loadbalancers need a public-IP pool. Add one in <strong>IP Pools</strong>.</>
                        )}
                    </span>
                )}
                {ipPools.length > 0 && !effectivePoolId && <span className="text-amber-400">⚠ No IP pool selected — assign one above or set a default on the cluster.</span>}
                {effectivePoolId && preview && (
                    <>
                        <div className="text-emerald-300" data-testid={`asv-${kind}-preview-ip`}>
                            Next free IP: <strong>{preview.next_ip || "—"}</strong> · gateway {preview.gateway}
                        </div>
                        <div className="text-zinc-500">Pool capacity: {preview.free_count} of {preview.total_count} free</div>
                    </>
                )}
            </div>

            <div className="grid grid-cols-3 gap-2">
                <Field label="CPU cores" type="number" min={1} v={form.cpu} on={(v) => setForm({ ...form, cpu: parseInt(v, 10) || 1 })} testid={`asv-${kind}-cpu`} />
                <Field label="RAM (MB)"  type="number" min={256} v={form.ram_mb} on={(v) => setForm({ ...form, ram_mb: parseInt(v, 10) || 1024 })} testid={`asv-${kind}-ram`} />
                <Field label="Disk (GB)" type="number" min={1} v={form.disk_gb} on={(v) => setForm({ ...form, disk_gb: parseInt(v, 10) || 10 })} testid={`asv-${kind}-disk`} />
            </div>

            {kind !== "caddy" && !presetPoolId && (
                <div className="grid grid-cols-2 gap-2">
                    <Field
                        label="Max Clouds (capacity of this VM)"
                        type="number"
                        min={1}
                        v={form.max_clouds}
                        on={(v) => setForm({ ...form, max_clouds: parseInt(v, 10) || 1 })}
                        testid={`asv-${kind}-max-clouds`}
                    />
                    <div className="text-[11px] text-zinc-500 self-end pb-2 leading-tight">
                        Eine eigene Standalone-Pool wird automatisch angelegt
                        (<code className="text-zinc-400">{form.pool_name}</code>). Tenants
                        verteilen sich auf die Pool-Member bis max_clouds erreicht ist.
                    </div>
                </div>
            )}

            {progress && (
                <div className="text-emerald-300 text-xs font-mono" data-testid={`asv-${kind}-progress`}>
                    ⏳ {progress}
                </div>
            )}
            {err && <div className="text-red-400 text-xs font-mono break-all">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit"
                         disabled={busy || !effectiveClusterId || !effectivePoolId}
                         className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                         data-testid={`asv-${kind}-submit`}>
                    {busy ? "Working…" : `Deploy ${label} VM`}
                </button>
            </div>
        </form>
    );
}

/**
 * Modal: provision a CM-managed service VM (Redis / Collabora / Caddy) on a
 * control-plane node. Auto-registers the resulting VM as a member of the
 * given pool.
 */
function ProvisionVmForm({ kind, pool, onClose }) {
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const { data: ipPoolsRaw } = useSWR("/api/admin/infra/host-networks", fetcher);
    // Only offer pools of the purpose this kind needs — caddy/coturn need a
    // PUBLIC pool (TURN/LB must be publicly reachable); everything else is a
    // service-zone VM (notify_push, redis, collabora, talk-signaling) and MUST
    // live in the SDN service zone. Prevents picking a wrong (public) subnet.
    const wantedPurpose = (kind === "caddy" || kind === "coturn") ? "public" : "service";
    const ipPools = (ipPoolsRaw || []).filter(
        (p) => (p.purpose || "public") === wantedPurpose,
    );
    const defaults = {
        redis:           { cpu: 1, ram_mb: 2048, disk_gb: 10 },
        collabora:       { cpu: 2, ram_mb: 4096, disk_gb: 20 },
        notify_push:     { cpu: 1, ram_mb: 1024, disk_gb: 10 },
        "talk-signaling":{ cpu: 2, ram_mb: 4096, disk_gb: 20 },
        coturn:          { cpu: 1, ram_mb: 1024, disk_gb: 10 },
        caddy:           { cpu: 1, ram_mb: 1024, disk_gb: 10 },
    }[kind] || { cpu: 1, ram_mb: 1024, disk_gb: 10 };
    const [form, setForm] = useState({
        cluster_id: null,
        host_network_id: null,  // null = use cluster default
        ...defaults,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    // Resolve cluster_id + host_network_id at render time
    const effectiveClusterId = form.cluster_id ?? clusters?.[0]?.id ?? "";
    const activeCluster = (clusters || []).find((c) => c.id === effectiveClusterId);
    // Only honour the cluster default pool if it matches the wanted purpose —
    // otherwise (e.g. cluster default is a public pool) force an explicit pick.
    const clusterDefaultPoolId =
        (ipPools || []).some((p) => p.id === activeCluster?.host_network_id)
            ? activeCluster.host_network_id
            : null;
    const effectivePoolId = form.host_network_id ?? clusterDefaultPoolId ?? "";

    // Live preview of next free IP
    const { data: preview } = useSWR(
        effectivePoolId ? `/api/admin/infra/host-networks/${effectivePoolId}/next-ip` : null,
        fetcher,
        { refreshInterval: 5000 },
    );

    const submit = async (e) => {
        e.preventDefault(); setErr(null); setBusy(true);
        try {
            await api.post("/api/admin/infra/service-vms", {
                cluster_id: effectiveClusterId,
                kind,
                pool_id: pool.id,
                cpu: form.cpu,
                ram_mb: form.ram_mb,
                disk_gb: form.disk_gb,
                // host_network_id override forwarded if user picked a non-default pool
                ...(form.host_network_id ? { host_network_id: form.host_network_id } : {}),
            });
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} className="mt-3 cm-card p-3 space-y-2 border border-[#CCFF00]" data-testid={`provision-vm-form-${kind}`}>
            <div className="text-xs uppercase tracking-widest font-mono text-[#CCFF00]">
                Provision new {kind} VM → pool {pool.name}
            </div>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                <select value={effectiveClusterId} onChange={(e) => setForm({ ...form, cluster_id: e.target.value, host_network_id: null })} className="cm-input w-full" data-testid="pv-cluster">
                    {(clusters || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </label>
            <label className="block">
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                    IP Pool {form.host_network_id ? "(override)" : "(cluster default)"}
                </span>
                <select value={effectivePoolId} onChange={(e) => setForm({ ...form, host_network_id: e.target.value })} className="cm-input w-full" data-testid="pv-ip-pool">
                    <option value="">— pick a pool —</option>
                    {(ipPools || []).map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name} — {p.subnet}
                        </option>
                    ))}
                </select>
            </label>
            <div className="bg-zinc-950 border border-zinc-800 p-2 text-[11px] font-mono">
                {ipPools.length === 0 && (
                    <span className="text-amber-400">
                        ⚠ No <strong>{wantedPurpose}</strong>-purpose IP pools defined.{" "}
                        {wantedPurpose === "service"
                            ? <>This service must run inside the SDN service zone. Go to <strong>IP Pools → Bootstrap Service Zone</strong> first.</>
                            : <>This service needs a public-IP pool. Add one in <strong>IP Pools</strong>.</>}
                    </span>
                )}
                {ipPools.length > 0 && !effectivePoolId && <span className="text-amber-400">⚠ No IP pool selected — pick a {wantedPurpose} pool above.</span>}
                {effectivePoolId && preview && (
                    <>
                        <div className="text-emerald-300" data-testid="pv-preview-ip">
                            Next free IP: <strong>{preview.next_ip || "—"}</strong> · gateway {preview.gateway}
                        </div>
                        <div className="text-zinc-500">Pool capacity: {preview.total_count - preview.free_count + 1}/{preview.total_count} would be used after this VM</div>
                    </>
                )}
            </div>
            <div className="grid grid-cols-3 gap-2">
                <Field label="CPU cores" type="number" v={form.cpu} on={(v) => setForm({ ...form, cpu: parseInt(v, 10) || 1 })} testid="pv-cpu" />
                <Field label="RAM (MB)" type="number" v={form.ram_mb} on={(v) => setForm({ ...form, ram_mb: parseInt(v, 10) || 1024 })} testid="pv-ram" />
                <Field label="Disk (GB)" type="number" v={form.disk_gb} on={(v) => setForm({ ...form, disk_gb: parseInt(v, 10) || 10 })} testid="pv-disk" />
            </div>
            <div className="text-[11px] text-zinc-500 italic">
                The VM lands on a control-plane node, gets the IP shown above, and is installed via cloud-init. Provisioning takes 60-180s.
            </div>
            {err && <div className="text-red-400 text-xs font-mono break-all">{err}</div>}
            <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !effectiveClusterId || !effectivePoolId} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="pv-submit">
                    {busy ? "Provisioning…" : "Provision"}
                </button>
            </div>
        </form>
    );
}

// =====================================================================
// AppStore-Caching-Proxy — single shared service-VM that caches the
// NC App-Store catalogue + release tarballs so tenant clouds don't
// individually hit apps.nextcloud.com (and get rate-limited on the
// shared egress IP).
// =====================================================================
export function AppStoreProxyPanel() {
    const { data: vms, mutate, error } = useSWR(
        "/api/admin/infra/service-vms?kind=appstore-proxy", fetcher,
        { refreshInterval: 5000 },
    );
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const { data: ipPoolsRaw } = useSWR("/api/admin/infra/host-networks", fetcher);
    const { data: egressVms } = useSWR(
        "/api/admin/infra/service-vms?kind=egress-gw", fetcher,
    );
    const servicePools = (ipPoolsRaw || []).filter(p => (p.purpose || "public") === "service");
    const [showDeploy, setShowDeploy] = useState(false);
    const [statsFor, setStatsFor] = useState(null);  // vmId currently being viewed
    const [stats, setStats] = useState({});           // vmId → stats payload
    const [statsBusy, setStatsBusy] = useState({});
    const [busyAction, setBusyAction] = useState({});
    const [error_, setError_] = useState(null);

    const proxies = useMemo(() => vms || [], [vms]);
    const active = useMemo(() => proxies.filter(v => v.status === "active"), [proxies]);
    const installing = useMemo(
        () => proxies.filter(v => ["installing", "starting", "provisioning"].includes(v.status)),
        [proxies],
    );
    const installingKey = useMemo(
        () => installing.map(v => v.id + v.status).join(","),
        [installing],
    );
    const activeKey = useMemo(() => active.map(v => v.id).join(","), [active]);

    // Auto-probe still-installing VMs so the status flips to "active" as
    // soon as cloud-init finishes (same pattern as the Caddy panel).
    useEffect(() => {
        installing.forEach((v) => {
            api.post(`/api/admin/infra/service-vms/${v.id}/health`)
                .catch(() => {}).finally(() => mutate());
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installingKey]);

    // Auto-load stats for every active proxy so the panel always shows
    // the cache-hit ratio prominently. Without this the operator has to
    // click "Stats" on every VM, which made the user complain "ich seh
    // die usage anzeige nicht" — now it's always visible.
    useEffect(() => {
        active.forEach((v) => {
            if (stats[v.id] !== undefined || statsBusy[v.id]) return;
            onLoadStats(v.id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey]);

    const refreshFirewallOnAllEgress = async () => {
        const targets = (egressVms || []).filter(v => v.status === "active");
        if (targets.length === 0) {
            setError_("No active egress-gw VM found — tenants can't reach the proxy yet.");
            return;
        }
        setBusyAction(b => ({ ...b, _refreshFw: true }));
        setError_(null);
        try {
            for (const t of targets) {
                await api.post(`/api/admin/infra/service-vms/${t.id}/refresh-firewall`);
            }
        } catch (e) {
            setError_(e?.response?.data?.detail || String(e));
        } finally {
            setBusyAction(b => ({ ...b, _refreshFw: false }));
        }
    };

    const onProbe = async (id) => {
        setBusyAction(b => ({ ...b, [id]: "probe" }));
        try { await api.post(`/api/admin/infra/service-vms/${id}/health`); }
        catch (e) { setError_(e?.response?.data?.detail || String(e)); }
        finally { setBusyAction(b => ({ ...b, [id]: undefined })); mutate(); }
    };

    const onDelete = async (id) => {
        if (!confirm("Destroy this AppStore-Proxy VM?\n\nTenant clouds will fall back to the public NC App Store on their next worker heartbeat.")) return;
        setBusyAction(b => ({ ...b, [id]: "delete" }));
        try { await api.delete(`/api/admin/infra/service-vms/${id}`); }
        catch (e) { setError_(e?.response?.data?.detail || String(e)); }
        finally { setBusyAction(b => ({ ...b, [id]: undefined })); mutate(); }
    };

    const onLoadStats = async (id) => {
        setStatsFor(id);
        setStatsBusy(s => ({ ...s, [id]: true }));
        try {
            const r = await api.get(`/api/admin/infra/service-vms/${id}/appstore-stats`);
            setStats(s => ({ ...s, [id]: r.data }));
            setError_(null);
        } catch (e) {
            setError_(`Stats failed: ${e?.response?.data?.detail || String(e)}`);
        } finally {
            setStatsBusy(s => ({ ...s, [id]: false }));
        }
    };

    if (!vms && !error) return <Spinner />;
    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">AppStore Caching Proxy</strong> — single shared service-VM that fronts <code>apps.nextcloud.com</code> for every tenant cloud.
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    <li>nginx + <code>proxy_cache</code> (1 GB on disk, 1h catalogue TTL, 24h release-tarball TTL)</li>
                    <li>operator-controlled <strong>allow/deny lists</strong> at <code>/etc/cm-appstore/{`{allow,deny}`}.list</code> on the VM (live-reload via systemd path-watcher)</li>
                    <li>Tenant worker NCs are auto-pointed at this proxy on their next heartbeat (see <code>Settings → appstore_proxy_enabled</code> for the cluster-wide kill-switch)</li>
                </ul>
                <div className="mt-1 text-amber-300/80">
                    ⚠ After deploying: click <strong>Refresh Egress Firewall</strong> below so tenant→proxy traffic on port 80 is whitelisted on every egress-gw.
                </div>
            </div>

            <div className="flex justify-end gap-2">
                {active.length > 0 && (
                    <button
                        data-testid="appstore-proxy-refresh-fw-btn"
                        onClick={refreshFirewallOnAllEgress}
                        disabled={busyAction._refreshFw}
                        className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1 border border-amber-700 text-amber-300 disabled:opacity-50"
                        title="Re-push iptables rules on every egress-gw so the new proxy IP is whitelisted"
                    >
                        <RefreshCw size={12} /> {busyAction._refreshFw ? "Refreshing…" : "Refresh Egress Firewall"}
                    </button>
                )}
                <button
                    data-testid="appstore-proxy-deploy-btn"
                    onClick={() => setShowDeploy(true)}
                    disabled={servicePools.length === 0}
                    className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                    title={servicePools.length === 0 ? "Bootstrap a Service-Zone pool first (IP Pools tab)" : ""}
                >
                    <Plus size={12} /> Deploy AppStore Proxy
                </button>
            </div>

            {error_ && <div className="text-red-400 text-xs font-mono break-all" data-testid="appstore-proxy-error">{error_}</div>}

            {showDeploy && (
                <DeployAppStoreProxyForm
                    clusters={clusters || []}
                    pools={servicePools}
                    onClose={() => { setShowDeploy(false); mutate(); }}
                />
            )}

            {proxies.length === 0 && !showDeploy && (
                <Empty title="No AppStore-Proxy deployed" hint="Tenant clouds currently hit apps.nextcloud.com directly via the egress-gw IP and may get rate-limited." />
            )}

            <div className="space-y-2">
                {proxies.map((v) => (
                    <div key={v.id} className="cm-card p-3" data-testid={`appstore-proxy-${v.id}`}>
                        <div className="flex items-start gap-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm">{v.hostname || v.id.slice(0, 12)}</span>
                                    <StatusBadge status={v.status} />
                                </div>
                                <div className="text-[11px] text-zinc-500 font-mono mt-1">
                                    IP: <span className="text-zinc-300">{v.ip_internal || "—"}</span>
                                    {" · "}
                                    Node: <span className="text-zinc-300">{v.node || "—"}</span>
                                    {" · "}
                                    VMID: <span className="text-zinc-300">{v.vmid || "—"}</span>
                                </div>
                                {v.status === "active" && (
                                    <div className="text-[11px] text-emerald-400/80 font-mono mt-0.5">
                                        Tenant URL: <code>http://{v.ip_internal}/v1</code>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <button
                                    data-testid={`appstore-proxy-health-${v.id}`}
                                    onClick={() => onProbe(v.id)}
                                    disabled={!!busyAction[v.id]}
                                    className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 disabled:opacity-50"
                                    title="Re-probe health"
                                >
                                    <Activity size={10} /> {busyAction[v.id] === "probe" ? "…" : "Probe"}
                                </button>
                                {v.status === "active" && (
                                    <button
                                        data-testid={`appstore-proxy-stats-${v.id}`}
                                        onClick={() => onLoadStats(v.id)}
                                        disabled={statsBusy[v.id]}
                                        className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-sky-700 text-sky-300 disabled:opacity-50"
                                        title="Show live cache-hit ratio + connection counts"
                                    >
                                        <FlaskConical size={10} /> {statsBusy[v.id] ? "…" : "Stats"}
                                    </button>
                                )}
                                <button
                                    data-testid={`appstore-proxy-delete-${v.id}`}
                                    onClick={() => onDelete(v.id)}
                                    disabled={!!busyAction[v.id]}
                                    className="cm-btn-ghost text-[11px] px-2 py-1 text-red-300 flex items-center gap-1 disabled:opacity-50"
                                >
                                    <Trash2 size={10} /> {busyAction[v.id] === "delete" ? "…" : "Delete"}
                                </button>
                            </div>
                        </div>

                        {stats[v.id] && (
                            <div className="mt-3 pt-3 border-t border-zinc-800 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`appstore-proxy-stats-result-${v.id}`}>
                                <StatTile label="Hit Ratio" value={`${Math.round((stats[v.id].cache?.HIT_RATIO || 0) * 100)}%`} accent="emerald" />
                                <StatTile label="Cache HITs" value={stats[v.id].cache?.HIT || 0} />
                                <StatTile label="Cache MISSes" value={stats[v.id].cache?.MISS || 0} />
                                <StatTile label="Bytes from Cache" value={formatBytes(stats[v.id].cache?.BYTES_HIT || 0)} />
                                <StatTile label="Cache on disk" value={formatBytes(stats[v.id].disk?.used || 0)} />
                                <StatTile label="Disk free" value={formatBytes(stats[v.id].disk?.fs_free || 0)} />
                                <StatTile label="Active conns" value={stats[v.id].stub_status?.active ?? "—"} />
                                <StatTile label="Total reqs" value={stats[v.id].stub_status?.requests ?? "—"} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatTile({ label, value, accent = "zinc" }) {
    const colorClass = accent === "emerald"
        ? "text-emerald-300 border-emerald-900 bg-emerald-950/30"
        : "text-zinc-200 border-zinc-800 bg-zinc-950";
    return (
        <div className={`p-2 border ${colorClass}`}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
            <div className="text-base font-mono">{value}</div>
        </div>
    );
}

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function DeployAppStoreProxyForm({ clusters, pools, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        host_network_id: pools?.[0]?.id || "",
        cpu: 1, ram_mb: 2048, disk_gb: 50,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const { data: preview } = useSWR(
        form.host_network_id ? `/api/admin/infra/host-networks/${form.host_network_id}/next-ip` : null,
        fetcher,
    );

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            await api.post("/api/admin/infra/service-vms", {
                cluster_id: form.cluster_id,
                kind: "appstore-proxy",
                host_network_id: form.host_network_id,
                cpu: form.cpu, ram_mb: form.ram_mb, disk_gb: form.disk_gb,
            });
            setTimeout(onClose, 800);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-3 border border-[#CCFF00]" data-testid="appstore-proxy-deploy-form">
            <div className="cm-h3">Deploy AppStore-Proxy VM</div>
            <div className="text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 p-2">
                A Debian-13 VM is cloned on a control-plane node into the
                service-zone, gets the next free IP shown below, and
                installs nginx + proxy_cache via cloud-init. Total time:
                60-120 seconds. Idempotent — re-running is safe.
            </div>
            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                    <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })}
                             className="cm-input w-full" data-testid="asp-cluster">
                        {clusters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Service-Zone IP Pool</span>
                    <select value={form.host_network_id} onChange={(e) => setForm({ ...form, host_network_id: e.target.value })}
                             className="cm-input w-full" data-testid="asp-pool">
                        {pools.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name} — {p.subnet}{p.bridge ? `  →  ${p.bridge}` : ""}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 p-2 text-[11px] font-mono">
                {preview ? (
                    <div className="text-emerald-300" data-testid="asp-preview-ip">
                        Next free IP: <strong>{preview.next_ip}</strong> · gateway {preview.gateway}
                    </div>
                ) : <span className="text-zinc-500">— select a pool —</span>}
            </div>
            <div className="grid grid-cols-3 gap-2">
                <Field label="CPU cores" type="number" min={1} v={form.cpu} on={(v) => setForm({ ...form, cpu: parseInt(v, 10) || 1 })} testid="asp-cpu" />
                <Field label="RAM (MB)"  type="number" min={256} v={form.ram_mb} on={(v) => setForm({ ...form, ram_mb: parseInt(v, 10) || 1024 })} testid="asp-ram" />
                <Field label="Disk (GB)" type="number" min={5} v={form.disk_gb} on={(v) => setForm({ ...form, disk_gb: parseInt(v, 10) || 10 })} testid="asp-disk" />
            </div>
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="asp-err">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id || !form.host_network_id}
                         className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                         data-testid="asp-submit">
                    {busy ? "Provisioning…" : "Deploy"}
                </button>
            </div>
        </form>
    );
}



// =====================================================================
// BIMI connector — one shared, stateless S3⇄LB proxy VM for the fleet.
// Serves `<slug>.souvera.work/bimi/<key>` by streaming objects from the
// shared `cm-bimi` Garage bucket. Logos/certs are uploaded per Souvera
// cloud via the admin API (POST /api/admin/clouds/{id}/bimi/logo|vmc).
// =====================================================================
export function BimiConnectorPanel() {
    const { data: vms, mutate, error } = useSWR(
        "/api/admin/infra/service-vms?kind=bimi", fetcher,
        { refreshInterval: 5000 });
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const { data: ipPoolsRaw } = useSWR("/api/admin/infra/host-networks", fetcher);
    const servicePools = (ipPoolsRaw || []).filter(
        (p) => (p.purpose || "public") === "service");
    const [showDeploy, setShowDeploy] = useState(false);
    const [busyAction, setBusyAction] = useState({});
    const [err_, setErr_] = useState(null);

    const connectors = useMemo(() => vms || [], [vms]);
    const installing = useMemo(
        () => connectors.filter((v) => ["installing", "starting", "provisioning"].includes(v.status)),
        [connectors]);
    const installingKey = installing.map((v) => v.id + v.status).join(",");

    useEffect(() => {
        installing.forEach((v) => {
            api.post(`/api/admin/infra/service-vms/${v.id}/health`)
                .catch(() => {}).finally(() => mutate());
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installingKey]);

    const onProbe = async (id) => {
        setBusyAction((b) => ({ ...b, [id]: "probe" }));
        try { await api.post(`/api/admin/infra/service-vms/${id}/health`); }
        catch (e) { setErr_(e?.response?.data?.detail || String(e)); }
        finally { setBusyAction((b) => ({ ...b, [id]: undefined })); mutate(); }
    };
    const onDelete = async (id) => {
        if (!confirm("Delete BIMI connector VM?\n\nUntil a new one is deployed, the LB no longer serves /bimi/* (mail logos disappear for clients).")) return;
        setBusyAction((b) => ({ ...b, [id]: "delete" }));
        try { await api.delete(`/api/admin/infra/service-vms/${id}`); }
        catch (e) { setErr_(e?.response?.data?.detail || String(e)); }
        finally { setBusyAction((b) => ({ ...b, [id]: undefined })); mutate(); }
    };

    if (!vms && !error) return <Spinner />;
    return (
        <div className="space-y-4" data-testid="bimi-connector-panel">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">BIMI Connector</strong> — eine geteilte,
                stateless service VM (S3&nbsp;⇄&nbsp;VM&nbsp;⇄&nbsp;LB). Streams brand logos &amp;
                VMC/CMC-Zertifikate aus dem gemeinsamen <code>cm-bimi</code> Garage-Bucket.
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    <li>Die LB routet <code>&lt;slug&gt;.souvera.work/bimi/*</code> hierher; ausgeliefert wird read-only aus S3.</li>
                    <li>Upload per Souvera cloud via the admin API: <code>POST /api/admin/clouds/&#123;id&#125;/bimi/logo</code> (SVG → BIMI-compliant) and <code>/bimi/vmc</code> (VMC/CMC).</li>
                    <li>Der <code>default._bimi</code>-DNS-Record erscheint automatisch bei den DNS-Records der Cloud (erfordert DMARC ≥ quarantine).</li>
                    <li>One connector VM per cluster is enough.</li>
                </ul>
            </div>

            {err_ && <div className="text-red-400 text-xs font-mono break-all" data-testid="bimi-err">{err_}</div>}

            <div className="flex justify-end">
                <button data-testid="bimi-deploy-btn" onClick={() => setShowDeploy(true)}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1">
                    <Plus size={12} /> Deploy BIMI Connector
                </button>
            </div>

            {showDeploy && (
                <DeployBimiConnectorForm clusters={clusters || []} pools={servicePools}
                                         onClose={() => { setShowDeploy(false); mutate(); }} />
            )}

            {connectors.length === 0 && !showDeploy ? (
                <Empty title="Noch keine BIMI-Connector-VM"
                       hint="Deploy a connector VM so /bimi/* is served via the LB." />
            ) : (
                <div className="space-y-2">
                    {connectors.map((v) => (
                        <div key={v.id} className="cm-card p-3 flex items-center justify-between"
                             data-testid={`bimi-vm-${v.id}`}>
                            <div className="text-xs">
                                <div className="font-mono text-zinc-200">{v.host || v.ip_internal || "—"}:8088</div>
                                <div className="text-[11px] text-zinc-500 mt-0.5">
                                    Status: <span className={v.status === "active" ? "text-emerald-400" : "text-amber-400"}>{v.status}</span>
                                    {"  ·  "}VMID: {v.vmid || "—"}{"  ·  "}Cluster: {v.cluster_id || "—"}
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button data-testid={`bimi-health-${v.id}`} onClick={() => onProbe(v.id)}
                                        disabled={!!busyAction[v.id]}
                                        className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 disabled:opacity-50">
                                    <Activity size={10} /> {busyAction[v.id] === "probe" ? "…" : "Probe"}
                                </button>
                                <button data-testid={`bimi-delete-${v.id}`} onClick={() => onDelete(v.id)}
                                        disabled={!!busyAction[v.id]}
                                        className="cm-btn-ghost text-[11px] px-2 py-1 text-red-300 flex items-center gap-1 disabled:opacity-50">
                                    <Trash2 size={10} /> {busyAction[v.id] === "delete" ? "…" : "Delete"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DeployBimiConnectorForm({ clusters, pools, onClose }) {
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        host_network_id: pools?.[0]?.id || "",
        cpu: 1, ram_mb: 512, disk_gb: 5,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const { data: preview } = useSWR(
        form.host_network_id ? `/api/admin/infra/host-networks/${form.host_network_id}/next-ip` : null,
        fetcher);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setBusy(true);
        try {
            await api.post("/api/admin/infra/service-vms", {
                cluster_id: form.cluster_id,
                kind: "bimi",
                host_network_id: form.host_network_id,
                cpu: form.cpu, ram_mb: form.ram_mb, disk_gb: form.disk_gb,
            });
            setTimeout(onClose, 800);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-3 border border-[#CCFF00]" data-testid="bimi-deploy-form">
            <div className="cm-h3">Deploy BIMI Connector VM</div>
            <div className="text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 p-2">
                Eine Debian-13-VM wird auf einem Control-Plane-Node in die Service-Zone geklont,
                gets the next free IP (see below) and installs a tiny Python-
                S3-Proxy (:8088) via cloud-init. Beim ersten Deploy wird der gemeinsame
                <code>cm-bimi</code>-Bucket samt Key idempotent angelegt.
            </div>
            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                    <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })}
                            className="cm-input w-full" data-testid="bimi-cluster">
                        {clusters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Service-Zone IP Pool</span>
                    <select value={form.host_network_id} onChange={(e) => setForm({ ...form, host_network_id: e.target.value })}
                            className="cm-input w-full" data-testid="bimi-pool">
                        {pools.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name} — {p.subnet}{p.bridge ? `  →  ${p.bridge}` : ""}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 p-2 text-[11px] font-mono">
                {preview ? (
                    <div className="text-emerald-300" data-testid="bimi-preview-ip">
                        Next free IP: <strong>{preview.next_ip}</strong> · gateway {preview.gateway}
                    </div>
                ) : <span className="text-zinc-500">— select a pool —</span>}
            </div>
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="bimi-deploy-err">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id || !form.host_network_id}
                        className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="bimi-submit">
                    {busy ? "Provisioning…" : "Deploy"}
                </button>
            </div>
        </form>
    );
}


// =====================================================================
// Notify Push — strict "1 VM = 1 pool" model.
// Click "Add Notify Push VM" → AddServiceVmFlow creates a NEW unique
// standalone pool (np-XXXXXX) and provisions exactly one VM into it; the
// service_vms route auto-registers it as the pool's single member. Existing
// pools expose no "Add VM"/"Provision VM" — add capacity via more VMs (each
// in its own pool). The operator just sees "I have N notify_push servers".
// =====================================================================
export function NotifyPushPanel() {
    const { data: pools, mutate: mutatePools } = useSWR(
        "/api/admin/infra/notify-push-pools", fetcher, { refreshInterval: 5000 });
    const [showAddVm, setShowAddVm] = useState(false);
    const [tests, setTests] = useState({});
    const [reconcile, setReconcile] = useState(null);  // {running, done, total, ok, failed, skipped}

    if (!pools) return <Spinner />;

    const onTest = async (poolId, mid) => {
        setTests((t) => ({ ...t, [mid]: { loading: true } }));
        try {
            const r = await api.post(`/api/admin/infra/notify-push-pools/${poolId}/members/${mid}/test`);
            setTests((t) => ({ ...t, [mid]: r.data }));
        } catch (e) {
            setTests((t) => ({ ...t, [mid]: { ok: false, error: e?.response?.data?.detail || String(e) } }));
        }
    };

    const onReconcileFleet = async () => {
        if (!confirm(
            "Reconfigure notify_push for ALL enabled clouds?\n\n" +
            "Schreibt pro Cloud die Push-.env neu (eindeutiger Redis-dbindex + " +
            "/etc/hosts Split-Horizon), startet die Unit neu und bumpt " +
            "config_version (NC & Binary landen auf demselben dbindex).\n\n" +
            "Idempotent, kein Teardown, kein Port-Wechsel.")) return;
        try {
            const list = (await api.get("/api/admin/infra/notify-push/enabled-clouds")).data;
            const ids = (list.clouds || []).map((c) => c.cloud_id);
            if (ids.length === 0) { alert("No cloud with notify_push enabled."); return; }
            let ok = 0, failed = 0, skipped = 0, done = 0;
            setReconcile({ running: true, total: ids.length, ok, failed, skipped, done });
            const CHUNK = 10;
            for (let i = 0; i < ids.length; i += CHUNK) {
                const chunk = ids.slice(i, i + CHUNK);
                const r = (await api.post("/api/admin/infra/notify-push/reconcile", { cloud_ids: chunk })).data;
                ok += r.ok || 0; failed += r.failed || 0; skipped += r.skipped || 0; done += chunk.length;
                setReconcile({ running: done < ids.length, total: ids.length, ok, failed, skipped, done });
            }
            setReconcile({ running: false, done: ids.length, total: ids.length, ok, failed, skipped });
        } catch (e) {
            setReconcile(null);
            alert("Reconcile fehlgeschlagen: " + (e?.response?.data?.detail || String(e)));
        }
    };

    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">Notify Push (notify_push)</strong> —
                Nextcloud&apos;s websocket push daemon. Replaces Files-app polling so clients learn about
                new files in real-time.
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    <li>Eine <strong>VM = ein Pool</strong> — „Add Notify Push VM" legt automatisch einen eigenen Standalone-Pool an und klont genau eine VM hinein.</li>
                    <li>More capacity = add more VMs (each gets its own pool). Tenants are automatically distributed to the least utilized pool.</li>
                    <li>Enabling it on a cloud installs <code>notify_push</code> directly + <code>occ notify_push:setup</code>.</li>
                    <li><strong>"Reconfigure fleet"</strong> rewrites the push <code>.env</code> on all enabled clouds (unique Redis dbindex + <code>/etc/hosts</code> split-horizon) and bumps config_version — required once after a deploy that changed the dbindex/split-horizon logic.</li>
                </ul>
            </div>

            <div className="flex justify-end gap-2 items-center">
                {reconcile && (
                    <span data-testid="notify-push-reconcile-status" className="text-xs text-zinc-400 mr-auto">
                        {reconcile.running ? "Reconcile running… " : "Reconcile done · "}
                        {reconcile.done}/{reconcile.total} · ok {reconcile.ok} · failed {reconcile.failed} · skipped {reconcile.skipped}
                    </span>
                )}
                <button data-testid="notify-push-reconcile-fleet-btn" onClick={onReconcileFleet}
                        disabled={reconcile?.running}
                        className="cm-btn-secondary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50">
                    <RefreshCw size={12} className={reconcile?.running ? "animate-spin" : ""} /> Flotte neu konfigurieren
                </button>
                <button data-testid="notify-push-add-vm-btn" onClick={() => setShowAddVm(true)}
                         className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1">
                    <Plus size={12} /> Add Notify Push VM
                </button>
            </div>
            {showAddVm && (
                <AddServiceVmFlow kind="notify_push" onClose={() => { setShowAddVm(false); mutatePools(); }} />
            )}

            {(pools || []).length === 0 && !showAddVm ? (
                <Empty title="No Notify Push servers yet" hint="Add the first VM to enable real-time Files-app updates." />
            ) : (
                <div className="space-y-4">
                    {(pools || []).map((p) => (
                        <PoolCard key={p.id} pool={p}
                                   apiBase="/api/admin/infra/notify-push-pools"
                                   metaLine={`${p.type} · ${p.region || "default"} · ${p.current_clouds || 0}/${p.max_clouds} clouds`}
                                   onMutate={mutatePools}
                                   memberDisplay={(m) => `${m.host}:${m.port}${m.public_url ? " (public: " + m.public_url + ")" : ""}`}
                                   memberTests={tests} onTestMember={onTest}
                                   testidPrefix="notify-push"
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// =====================================================================
// Antivirus ICAP-Pool — zentraler Virenscan.
//   * avscan-lb : EIN HAProxy-LB (TCP :1344) pro Cluster → verteilt auf …
//   * avscan    : horizontal skalierbare ClamAV/c-icap-Scan-Nodes.
// Clouds nutzen files_antivirus (ICAP) gegen die feste avscan-lb-IP:1344.
// =====================================================================
export function AntivirusPanel() {
    const { data: lbs, mutate: mutateLb } = useSWR(
        "/api/admin/infra/service-vms?kind=avscan-lb", fetcher, { refreshInterval: 5000 });
    const { data: nodes, mutate: mutateNodes } = useSWR(
        "/api/admin/infra/service-vms?kind=avscan", fetcher, { refreshInterval: 5000 });
    const { data: ipPoolsRaw } = useSWR("/api/admin/infra/host-networks", fetcher);
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const servicePools = (ipPoolsRaw || []).filter(p => (p.purpose || "public") === "service");

    const [showDeploy, setShowDeploy] = useState(null);  // "avscan-lb" | "avscan" | null
    const [busy, setBusy] = useState({});
    const [err, setErr] = useState(null);

    const allVms = useMemo(() => [...(lbs || []), ...(nodes || [])], [lbs, nodes]);
    const installing = useMemo(
        () => allVms.filter(v => ["installing", "starting", "provisioning"].includes(v.status)),
        [allVms]);
    const installingKey = useMemo(() => installing.map(v => v.id + v.status).join(","), [installing]);
    const mutateAll = () => { mutateLb(); mutateNodes(); };

    // Auto-probe still-installing VMs so status flips to "active".
    useEffect(() => {
        installing.forEach((v) => {
            api.post(`/api/admin/infra/service-vms/${v.id}/health`).catch(() => {}).finally(mutateAll);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installingKey]);

    const onProbe = async (id) => {
        setBusy(b => ({ ...b, [id]: "probe" }));
        try { await api.post(`/api/admin/infra/service-vms/${id}/health`); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(b => ({ ...b, [id]: undefined })); mutateAll(); }
    };
    const onDelete = async (id, kind) => {
        if (!confirm(kind === "avscan-lb"
            ? "Destroy the AV load balancer?\n\nClouds with antivirus enabled cannot scan afterwards until a new LB is deployed and av_host is re-set."
            : "Destroy the AV scan node?\n\nThe LB removes it from rotation automatically.")) return;
        setBusy(b => ({ ...b, [id]: "delete" }));
        try { await api.delete(`/api/admin/infra/service-vms/${id}`); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(b => ({ ...b, [id]: undefined })); mutateAll(); }
    };
    const onSyncLb = async () => {
        setBusy(b => ({ ...b, _sync: true }));
        setErr(null);
        try {
            const r = await api.post("/api/admin/infra/avscan/sync");
            if (!r.data?.ok) setErr(`LB-Sync: ${r.data?.reason || "nicht ok"}`);
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(b => ({ ...b, _sync: false })); mutateAll(); }
    };

    if (!lbs && !nodes) return <Spinner />;
    const lb = (lbs || [])[0];
    const activeNodes = (nodes || []).filter(v => v.status === "active");

    const VmRow = (v) => (
        <div key={v.id} className="cm-card p-3" data-testid={`avscan-vm-${v.id}`}>
            <div className="flex items-start gap-3">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 text-[10px] uppercase bg-zinc-800 text-zinc-300">{v.kind}</span>
                        <span className="font-mono text-sm">{v.hostname || v.id.slice(0, 12)}</span>
                        <StatusBadge status={v.status} />
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono mt-1">
                        IP: <span className="text-zinc-300">{v.ip_internal || "—"}</span>{" · "}
                        Node: <span className="text-zinc-300">{v.node || "—"}</span>{" · "}
                        VMID: <span className="text-zinc-300">{v.vmid || "—"}</span>
                    </div>
                    {v.kind === "avscan-lb" && v.status === "active" && (
                        <div className="text-[11px] text-emerald-400/80 font-mono mt-0.5">
                            ICAP endpoint for clouds: <code>{v.ip_internal}:1344</code>
                        </div>
                    )}
                </div>
                <div className="flex gap-1">
                    <button data-testid={`avscan-health-${v.id}`} onClick={() => onProbe(v.id)}
                            disabled={!!busy[v.id]}
                            className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 disabled:opacity-50">
                        <Activity size={10} /> {busy[v.id] === "probe" ? "…" : "Probe"}
                    </button>
                    <button data-testid={`avscan-delete-${v.id}`} onClick={() => onDelete(v.id, v.kind)}
                            disabled={!!busy[v.id]}
                            className="cm-btn-ghost text-[11px] px-2 py-1 text-red-300 flex items-center gap-1 disabled:opacity-50">
                        <Trash2 size={10} /> {busy[v.id] === "delete" ? "…" : "Delete"}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200 flex items-center gap-1"><ShieldCheck size={13} /> Antivirus ICAP-Pool</strong>
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    <li><strong>avscan-lb</strong> — EIN HAProxy-LB (TCP :1344) pro Cluster mit fester Service-Zonen-IP. Genau diese IP bekommen Clouds als <code>av_host</code>.</li>
                    <li><strong>avscan</strong> — Debian 13 + ClamAV + freshclam + c-icap, horizontal skalierbar. Strenge Host-Firewall (kein Zugriff auf S3/DB/Redis/Tenant).</li>
                    <li>Reihenfolge: zuerst <strong>≥1 Scan-Node</strong>, dann den <strong>LB</strong> deployen. Danach Antivirus pro Cloud unter <em>Cloud → Services</em> aktivieren.</li>
                </ul>
                <div className="mt-1 text-amber-300/80">⚠ Nach Add/Remove eines Scan-Nodes: <strong>LB synchronisieren</strong> (passiert automatisch, Button erzwingt es).</div>
            </div>

            <div className="flex justify-end gap-2 flex-wrap">
                <button data-testid="avscan-sync-lb-btn" onClick={onSyncLb} disabled={busy._sync || !lb}
                        className="cm-btn-ghost text-xs px-3 py-2 flex items-center gap-1 border border-sky-700 text-sky-300 disabled:opacity-50"
                        title={!lb ? "Zuerst einen avscan-lb deployen" : "HAProxy-Backendliste aus aktiven Scan-Nodes neu rendern + pushen"}>
                    <RefreshCw size={12} /> {busy._sync ? "Sync…" : "LB synchronisieren"}
                </button>
                <button data-testid="avscan-deploy-node-btn" onClick={() => setShowDeploy("avscan")}
                        disabled={servicePools.length === 0}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                        title={servicePools.length === 0 ? "Zuerst eine Service-Zone im Tab IP Pools bootstrappen" : ""}>
                    <Plus size={12} /> Add AV scan node
                </button>
                <button data-testid="avscan-deploy-lb-btn" onClick={() => setShowDeploy("avscan-lb")}
                        disabled={servicePools.length === 0 || !!lb}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                        title={lb ? "Es existiert bereits ein avscan-lb (einer pro Cluster)" : servicePools.length === 0 ? "Zuerst eine Service-Zone im Tab IP Pools bootstrappen" : ""}>
                    <Plus size={12} /> AV-Loadbalancer deployen
                </button>
            </div>

            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="avscan-error">{err}</div>}

            {showDeploy && (
                <DeployAvVmForm
                    kind={showDeploy}
                    clusters={clusters || []}
                    pools={servicePools}
                    onClose={() => { setShowDeploy(null); mutateAll(); }}
                />
            )}

            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mb-1">Loadbalancer</div>
                {lb ? VmRow(lb) : <Empty title="Kein AV-Loadbalancer" hint="Deploy einen avscan-lb, sobald ≥1 Scan-Node aktiv ist." />}
            </div>
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mb-1">
                    Scan-Nodes ({activeNodes.length} aktiv / {(nodes || []).length} gesamt)
                </div>
                {(nodes || []).length === 0
                    ? <Empty title="No scan nodes" hint="Add at least one avscan node." />
                    : <div className="space-y-2">{(nodes || []).map(VmRow)}</div>}
            </div>
        </div>
    );
}

function DeployAvVmForm({ kind, clusters, pools, onClose }) {
    const isLb = kind === "avscan-lb";
    const [form, setForm] = useState({
        cluster_id: clusters?.[0]?.id || "",
        host_network_id: pools?.[0]?.id || "",
        cpu: isLb ? 1 : 2, ram_mb: isLb ? 1024 : 4096, disk_gb: isLb ? 10 : 20,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const { data: preview } = useSWR(
        form.host_network_id ? `/api/admin/infra/host-networks/${form.host_network_id}/next-ip` : null, fetcher);

    const submit = async (e) => {
        e.preventDefault(); setErr(null); setBusy(true);
        try {
            await api.post("/api/admin/infra/service-vms", {
                cluster_id: form.cluster_id, kind,
                host_network_id: form.host_network_id,
                cpu: form.cpu, ram_mb: form.ram_mb, disk_gb: form.disk_gb,
            });
            setTimeout(onClose, 800);
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="cm-card p-4 space-y-3 border border-[#CCFF00]" data-testid={`avscan-deploy-form-${kind}`}>
            <div className="cm-h3">{isLb ? "AV-Loadbalancer (avscan-lb) deployen" : "AV-Scan-Node (avscan) deployen"}</div>
            <div className="text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 p-2">
                {isLb
                    ? "HAProxy in TCP mode on :1344. Gets a fixed service-zone IP — exactly this IP is used as av_host for clouds."
                    : "Debian 13 VM with ClamAV + freshclam + c-icap. Loads the signature DB on boot (takes a few minutes), afterwards it is automatically registered as an LB backend."}
            </div>
            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Cluster</span>
                    <select value={form.cluster_id} onChange={(e) => setForm({ ...form, cluster_id: e.target.value })}
                            className="cm-input w-full" data-testid="av-cluster">
                        {clusters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Service-Zone IP Pool</span>
                    <select value={form.host_network_id} onChange={(e) => setForm({ ...form, host_network_id: e.target.value })}
                            className="cm-input w-full" data-testid="av-pool">
                        {pools.map((p) => (
                            <option key={p.id} value={p.id}>{p.name} — {p.subnet}{p.bridge ? `  →  ${p.bridge}` : ""}</option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 p-2 text-[11px] font-mono">
                {preview ? (
                    <div className="text-emerald-300" data-testid="av-preview-ip">
                        Next free IP: <strong>{preview.next_ip}</strong> · Gateway {preview.gateway}
                    </div>
                ) : <span className="text-zinc-500">— select pool —</span>}
            </div>
            <div className="grid grid-cols-3 gap-2">
                <Field label="CPU cores" type="number" min={1} v={form.cpu} on={(v) => setForm({ ...form, cpu: parseInt(v, 10) || 1 })} testid="av-cpu" />
                <Field label="RAM (MB)" type="number" min={512} v={form.ram_mb} on={(v) => setForm({ ...form, ram_mb: parseInt(v, 10) || 1024 })} testid="av-ram" />
                <Field label="Disk (GB)" type="number" min={5} v={form.disk_gb} on={(v) => setForm({ ...form, disk_gb: parseInt(v, 10) || 10 })} testid="av-disk" />
            </div>
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="av-err">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !form.cluster_id || !form.host_network_id}
                        className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="av-submit">
                    {busy ? "Provisioning…" : "Deploy"}
                </button>
            </div>
        </form>
    );
}

// Backward-compat export alias — old InfrastructurePage imports HpbPanel.
export const HpbPanel = NotifyPushPanel;
// Re-export AddServiceVmFlow for cross-panel re-use (Talk HPB needs it
// to provision talk-signaling + coturn VMs that auto-register in a pool).
export { AddServiceVmFlow };

